import type { StarGateRead } from "./star-gate";
import type { PostOutcome, StakePair } from "./add-resolution";
import type { StakeFacts, EventFacts } from "./betting-bus";
import { normaliseEvent, normaliseMatchResult, scoredMainLines, type MatchResultMarket, type MarketLine } from "./market-source";
import {
  buildPlaceBetCoupon,
  type BetBuilderBoard,
  type BetBuilderLeg,
  type PlaceBetAuth,
  type PlaceBetEvent,
  type PlaceBetResult,
} from "./place-bet";
import type {
  AdapterConfig,
  EventBusConfig,
  LiveMatch,
  Market,
  OddsEntry,
  SportsbookSource,
  BetslipSink,
  Suggestion,
} from "./types";
import { findRouterHistory } from "./route-source";

export class DomAdapter {
  private config: AdapterConfig;
  private attempts = 0;
  private misses = 0;

  constructor(config: AdapterConfig) {
    this.config = config;
  }

  getSportsbookDocument(parentDoc: Document): Document | null {
    try {
      const iframe = parentDoc.querySelector(
        this.config.iframe_selector
      ) as HTMLIFrameElement | null;
      if (!iframe) return null;
      const doc = iframe.contentDocument;
      if (!doc) return null;
      return doc;
    } catch {
      // Cross-origin access throws
      return null;
    }
  }

  scrapeMatches(doc: Document): LiveMatch[] {
    this.attempts++;
    const rows = doc.querySelectorAll(this.config.selectors.match_row);
    if (rows.length === 0) {
      this.misses++;
      return [];
    }

    const matches: LiveMatch[] = [];
    rows.forEach((row) => {
      const link = row.querySelector(
        this.config.selectors.match_link
      ) as HTMLAnchorElement | null;
      const href = link?.getAttribute("href") ?? "";

      const matchId = this.extractMatchId(href);
      const sport = this.extractSport(href);

      const competitorsEl = row.querySelector(this.config.selectors.competitors);
      const competitorsText = competitorsEl?.textContent?.trim() ?? "";

      const statusEl = row.querySelector(this.config.selectors.match_status);
      const rawStatus = statusEl?.textContent?.trim() ?? "";
      const status = rawStatus.toLowerCase();

      const timeEl = row.querySelector(this.config.selectors.match_time);
      const timeText = timeEl?.textContent?.trim() ?? "";

      // Parse minute from strings like "65'" or fall back to 0
      const minuteMatch = timeText.match(/^(\d+)['′]?$/);
      const minute = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;

      // Try to get team names from .sb-TeamColumn-name spans first
      const teamNameEls = (competitorsEl as Element)?.querySelectorAll(".sb-TeamColumn-name");
      let home = "";
      let away = "";
      if (teamNameEls && teamNameEls.length >= 2) {
        home = teamNameEls[0]?.textContent?.trim() ?? "";
        away = teamNameEls[1]?.textContent?.trim() ?? "";
      } else {
        // Fallback: split on " vs " or " - "
        const parts = competitorsText.split(/\s+vs\.?\s+|\s+-\s+/i);
        home = parts[0]?.trim() ?? "";
        away = parts[1]?.trim() ?? "";
      }

      matches.push({
        id: matchId,
        sport,
        league: "",
        home,
        away,
        score: "",
        minute,
        status,
      });
    });

    return matches;
  }

  scrapeOdds(doc: Document): OddsEntry[] {
    this.attempts++;
    const rows = doc.querySelectorAll(this.config.selectors.match_row);
    if (rows.length === 0) {
      this.misses++;
      return [];
    }

    const odds: OddsEntry[] = [];
    rows.forEach((row) => {
      const link = row.querySelector(
        this.config.selectors.match_link
      ) as HTMLAnchorElement | null;
      const href = link?.getAttribute("href") ?? "";
      const matchId = this.extractMatchId(href);

      // Get team names for selection labels
      const compEl = row.querySelector(this.config.selectors.competitors);
      const teamNameEls = compEl?.querySelectorAll(".sb-TeamColumn-name");
      const homeTeam = teamNameEls?.[0]?.textContent?.trim() ?? "Home";
      const awayTeam = teamNameEls?.[1]?.textContent?.trim() ?? "Away";
      const selectionLabels = [homeTeam, "Draw", awayTeam];

      // Take the first 3 UNLOCKED odds buttons (1X2 market: Home, Draw, Away)
      const buttons = row.querySelectorAll(this.config.selectors.odds_button);
      let collected = 0;
      for (let idx = 0; idx < buttons.length && collected < 3; idx++) {
        const btn = buttons[idx];
        // Skip locked buttons (padlock icon, no odds value)
        if (btn.querySelector(".sb-Odd-iconLock")) continue;
        const valueEl = btn.querySelector(this.config.selectors.odds_value);
        const rawValue = valueEl?.textContent?.trim() ?? "";
        const oddsValue = parseFloat(rawValue);
        if (isNaN(oddsValue)) continue;

        odds.push({
          match_id: matchId,
          market: "match_winner",
          selection: selectionLabels[collected] ?? `Selection ${collected + 1}`,
          odds: oddsValue,
        });
        collected++;
      }
    });

    return odds;
  }

  extractMatchId(href: string): string {
    const regex = new RegExp(this.config.id_extraction.match_id_regex);
    const match = regex.exec(href);
    return match?.[1] ?? "";
  }

  extractSport(href: string): string {
    const regex = new RegExp(this.config.id_extraction.sport_regex);
    const match = regex.exec(href);
    return match?.[1] ?? "";
  }

  getHealthStatus(): { ok: boolean; missRate: number } {
    const missRate = this.attempts === 0 ? 0 : this.misses / this.attempts;
    return { ok: missRate < 0.5, missRate };
  }

  clickOddsButton(doc: Document, selector: string): boolean {
    try {
      const el = doc.querySelector(selector) as HTMLElement | null;
      if (!el) return false;
      el.click();
      return true;
    } catch {
      return false;
    }
  }
}

// ---------------------------------------------------------------------------
// Digitain event-bus source & sink
//
// Unlike SoftSwiss (DOM scraping), Digitain exposes a supported two-way event
// bus via the operator's `window.SportFrame` wrapper. We capture the current
// event context from the `sportAppEventDispatch` routing event (verified live:
// type 3, message.qs = {sport,country,champ,event,live,supertip}, message
// .additionalData = {eventName,sportName,tournamentName,eventDate,team1,team2}).
//
// Two transports, per verified boot mode:
//  - iframe (Hannibalbet): the sportsbook posts cross-frame messages to the
//    parent — we catch them passively with window 'message'.
//  - native (Parikara): the sportsbook calls closure-bound integrator callbacks,
//    so the operator forwards events to us via window.BidCanvasCompanion.ingest().
//
// NOTE: the routing event carries NO odds. DigitainSource yields match context
// only; odds/coupon booking numbers come from the ranking backend + a Digitain
// data feed (TBD with the client). getOdds() is intentionally empty for now.
// ---------------------------------------------------------------------------

interface DigitainWindow extends Window {
  SportFrame?: Record<string, unknown>;
  BidCanvasCompanion?: { ingest?: (payload: unknown) => void };
}

export class DigitainSource implements SportsbookSource {
  private cfg: EventBusConfig;
  private currentMatch: LiveMatch | null = null;
  private attached = false;
  private onContext: (() => void) | null = null;
  private messageListener: ((e: MessageEvent) => void) | null = null;
  private ingestListener: ((payload: unknown) => void) | null = null;

  constructor(cfg: EventBusConfig) {
    this.cfg = cfg;
  }

  start(onContext: () => void): void {
    if (this.attached) return;
    this.attached = true;
    this.onContext = onContext;

    // iframe mode: passive cross-frame messages
    try {
      this.messageListener = (e: MessageEvent) => this.handleRaw(e.data);
      window.addEventListener("message", this.messageListener);
    } catch {
      // no window (test env may stub differently)
    }

    // native mode: operator forwards events here from their callback
    const w = window as unknown as DigitainWindow;
    w.BidCanvasCompanion = w.BidCanvasCompanion ?? {};
    this.ingestListener = (payload: unknown) => this.handleRaw(payload);
    w.BidCanvasCompanion.ingest = this.ingestListener;
  }

  stop(): void {
    if (this.messageListener) {
      try { window.removeEventListener("message", this.messageListener); } catch { /* no window */ }
      this.messageListener = null;
    }
    const w = window as unknown as DigitainWindow;
    if (w.BidCanvasCompanion?.ingest === this.ingestListener) delete w.BidCanvasCompanion.ingest;
    this.ingestListener = null;
    this.onContext = null;
    this.attached = false;
  }

  ready(): boolean {
    return this.attached;
  }

  getMatches(): LiveMatch[] {
    return this.currentMatch ? [this.currentMatch] : [];
  }

  getOdds(): OddsEntry[] {
    // Event bus carries no odds — ranking uses match context; odds/booking
    // number come from the Digitain data feed (TBD with client).
    return [];
  }

  // Accept any of the shapes we've seen: full postMessage envelope
  // {command, args:{data:{type,message}}}, or {data:{type,message}}, or
  // {type,message} directly (native callback may pass the inner object).
  private handleRaw(raw: unknown): void {
    const inner = this.extractRouting(raw);
    if (!inner) return;
    const match = this.toMatch(inner);
    if (!match) return;
    this.currentMatch = match;
    if (this.onContext) this.onContext();
  }

  private extractRouting(raw: unknown): { qs: Record<string, string>; additionalData: Record<string, string> } | null {
    if (!raw || typeof raw !== "object") return null;
    const r = raw as Record<string, any>;

    // Only handle our dispatch command when an envelope names one.
    if (typeof r.command === "string" && r.command !== this.cfg.dispatch_command) {
      return null;
    }

    // Dig down to the {type, message} object.
    const candidate =
      r.args?.data ?? // {command, args:{data:{type,message}}}
      r.data ?? //        {data:{type,message}}
      r; //               {type,message}

    if (!candidate || typeof candidate !== "object") return null;
    if (candidate.type !== this.cfg.routing_type) return null;

    const message = candidate.message;
    if (!message || typeof message !== "object") return null;

    const qs = (message.qs ?? {}) as Record<string, string>;
    const additionalData = (message.additionalData ?? {}) as Record<string, string>;
    return { qs, additionalData };
  }

  private toMatch(inner: {
    qs: Record<string, string>;
    additionalData: Record<string, string>;
  }): LiveMatch | null {
    const { qs, additionalData } = inner;
    const id = String(qs.event ?? "");
    if (!id) return null;
    return {
      id,
      sport: additionalData.sportName ?? "",
      league: additionalData.tournamentName ?? "",
      home: additionalData.team1 ?? "",
      away: additionalData.team2 ?? "",
      score: "",
      minute: 0,
      status: qs.live === "1" ? "live" : "prematch",
    };
  }
}

/** True when the sportsbook is rendered in a cross-origin IFRAME (Hannibalbet), as
 * opposed to natively in our own document (Parikara). Used to tell "native mode, post
 * to the parent" apart from "iframe mode, but we couldn't reach it" — which must fail
 * honestly rather than post into the void and claim the bet landed. */
function hasSportsbookIframe(): boolean {
  try {
    const frames = document.querySelectorAll("iframe");
    for (let i = 0; i < frames.length; i++) {
      const src = (frames[i] as HTMLIFrameElement).src || "";
      // Hannibalbet: `.../<GUID>/SportsBook/Home`. The RequestHelper iframe (present
      // on BOTH sites) is a bare API proxy and must not count as the sportsbook.
      if (src.indexOf("SportsBook") >= 0) return true;
    }
  } catch {
    /* cross-origin src reads can throw */
  }
  return false;
}

export class DigitainSink implements BetslipSink {
  private cfg: EventBusConfig;
  // TWO SETS, and the distinction is safety-critical.
  //
  // `posted` — legs we sent `toggleStake` for. NOT proof they landed: the operator
  // declines a stale or suspended stake (measured: "ERROR_STAKE_DOS_NOT_EXIST"/1304
  // when a stake id has rotated) without us knowing in the same tick.
  //
  // `added`  — legs the OPERATOR'S OWN COUPON confirmed, via confirmAdded(). Only
  // these may ever be toggled back off. toggleStake is symmetric, so re-posting a
  // leg that never landed ADDS it — which is exactly how the overlay came to write
  // bets into live coupons that the user never chose (2026-08-03).
  //
  // Conflating the two is the original defect. Do not merge these sets.
  private posted = new Set<string>();
  private added = new Set<string>();
  // A delayed resolver may outlive another successful add of the same leg.
  // Pair objects carry private ownership; the wire payload remains unchanged.
  private ownership = new Map<string, number>();
  private pairOwnership = new WeakMap<object, number>();
  private sharedPairs = new WeakSet<object>();
  private nextOwnership = 0;

  ownsLeg(pair: StakePair): boolean {
    const key = `${pair.eventId}:${pair.stakeId}`;
    const owner = this.pairOwnership.get(pair);
    return (owner == null || owner === this.ownership.get(key))
      && (this.posted.has(key) || this.added.has(key));
  }

  constructor(cfg: EventBusConfig) {
    this.cfg = cfg;
  }

  /**
   * Measured per-event liveness (wired to `catalog.eventFacts` by the
   * integration; null = neither board knows the event). `matchIsLive` on the
   * toggleStake app-event ROUTES Digitain's stake lookup: a pre-match stake id
   * posted with `matchIsLive: true` is looked up in the live catalogue and
   * refused as ERROR_STAKE_DOS_NOT_EXIST — proven live on Parikara 2026-09-01,
   * where the identical stake id was refused with true and accepted with false.
   */
  isEventLive: (eventId: number) => boolean | null = () => null;

  /** The flag to post for a leg: the catalog's measured answer at POST time
   * (fresher than the card — a fixture can kick off while the card sits open),
   * then the suggestion's own flag, then live — the dominant case, and the only
   * behaviour that existed before the pre-match pool shipped. */
  private legIsLive(eventId: number, fallback?: boolean): boolean {
    const measured = this.isEventLive(eventId);
    if (measured != null) return measured;
    return fallback ?? true;
  }

  /** A placed bet consumes the slip: forget the toggled stakes so a later re-add of
   * the same pick actually posts toggleStake again instead of being skipped as
   * "already in the slip" (which showed "Sent" with an empty slip). Wired to the
   * `successBet` pulse. */
  clearAdded(): void {
    this.added.clear();
    this.posted.clear();
    this.ownership.clear();
  }

  /**
   * The operator's own coupon confirmed these legs. Promotes posted -> added,
   * which is the ONLY way a leg becomes eligible for retraction.
   *
   * Callers must source this from `BettingBusWatcher.landedStakes()` — the
   * operator's answer — never from what they hoped would happen.
   */
  confirmAdded(pairs: Array<{ stakeId: number; eventId: number }>): void {
    for (const p of pairs) {
      const key = `${p.eventId}:${p.stakeId}`;
      const owner = this.pairOwnership.get(p);
      if (owner != null && owner !== this.ownership.get(key)) continue;
      if (this.sharedPairs.has(p) || !this.ownership.has(key)) {
        const next = ++this.nextOwnership;
        this.ownership.set(key, next);
        this.pairOwnership.set(p, next);
        this.sharedPairs.delete(p);
      }
      this.posted.delete(key);
      this.added.add(key);
    }
  }

  /**
   * Forget confirmed legs the operator's coupon no longer holds.
   *
   * `added` is written when the coupon confirms a leg and, until now, was cleared
   * only by a PLACED bet. A user who empties their slip by hand — or Digitain
   * dropping a leg by itself — left us holding a phantom for the rest of the
   * session, and a phantom is not a cosmetic problem: `addStakes` skips that leg,
   * so `confirmAdd` then waits on an id nobody will ever ack, the verdict can only
   * come back `partial`, and the partial sweep RETRACTS the legs that did land.
   * One stale entry turns a good add into a failed one and undoes it. Observed
   * live 2026-08-05: three legs all priced and live, `posted=2`, and the landed
   * leg vanished from the coupon a moment later.
   *
   * Only `added` is reconciled. `posted` is in flight — the coupon legitimately
   * does not show those yet, and dropping them would re-post a toggle that ADDS.
   */
  reconcileAdded(couponStakeIds: number[]): void {
    const inSlip = new Set(couponStakeIds);
    for (const key of [...this.added]) {
      const sid = Number(key.slice(key.indexOf(":") + 1));
      if (!inSlip.has(sid)) {
        this.added.delete(key);
        this.ownership.delete(key);
      }
    }
  }

  /**
   * Drop legs from our bookkeeping WITHOUT posting anything.
   *
   * For legs that never landed. They must not be retracted — `toggleStake` would
   * ADD them — and they must not linger in `added`, or a later retry is skipped
   * as "already in the slip" and the user taps a button that does nothing.
   */
  forget(pairs: Array<{ stakeId: number; eventId: number }>): void {
    for (const p of pairs) {
      const key = `${p.eventId}:${p.stakeId}`;
      const owner = this.pairOwnership.get(p);
      if (owner != null && owner !== this.ownership.get(key)) continue;
      this.posted.delete(key);
      this.added.delete(key);
      this.ownership.delete(key);
    }
  }

  /**
   * Add the recommended selection(s) to the operator betslip BY STAKE ID — the real
   * one-tap add, cross-origin, no booking number (live-proven 2026-07-07). Posts the
   * landing-widgets/bootstrapper app-event `{type:'toggleStake', sender:'bootstrapper',
   * data:{stakeId,eventId,matchIsLive,isSuperTip,promoId,cardId,factor}}` to the
   * sportsbook iframe (`SportFrame.elem.contentWindow`) — one post per combo leg —
   * then opens the betslip.
   *
   * Returns a `PostOutcome` splitting the legs THIS call posted from the ones an
   * earlier add already owns, or `null` when nothing could be posted (no reachable
   * window, or no valid stake ids) so the caller falls back to navigate-to-pick.
   * The caller must only ever mutate `posted` — see add-resolution.ts.
   */
  addStakes(suggestion: Suggestion, subEventOf?: (stakeId: number) => number | null): PostOutcome | null {
    try {
      const sf = (window as unknown as DigitainWindow).SportFrame as
        | Record<string, unknown>
        | undefined;
      if (!sf) return null;

      // Build the leg list: combo → each leg; single → the one pick.
      const rawLegs = (suggestion.legs && suggestion.legs.length)
        ? suggestion.legs.map((l) => ({
            stakeId: Number(l.stake_id), eventId: Number(l.event_id), factor: Number(l.odds),
            // ⛔ THE LEG'S OWN LIVENESS, and it must be the leg's: `matchIsLive`
            // routes the stake lookup, and an acca can hold a live fixture and one
            // 11 hours out. `_assemble_combo` states `is_live` per leg and the
            // post used to drop it, so a mixed acca sent its live legs to the
            // pre-match catalogue (or the reverse) and every leg was refused.
            isLive: typeof l.is_live === "boolean" ? l.is_live : undefined,
          }))
        : [{
            stakeId: Number(suggestion.stake_id),
            // ⛔ 2026-10-02: a shown corners single was rejected when toggleStake
            // used its match id; the same stake landed under its sub-event id.
            eventId: !suggestion.legs && typeof suggestion.part === "string" && suggestion.part
              ? (subEventOf?.(Number(suggestion.stake_id)) ?? NaN)
              : Number(suggestion.event_id),
            factor: Number(suggestion.odds),
          }];
      return this.postLegs(rawLegs, suggestion.is_live);
    } catch {
      return null;
    }
  }

  /**
   * Post specific (stakeId, eventId) pairs — the repair path, where the legs come
   * from re-resolving a dead outcome against the live board rather than from a
   * suggestion's own (by then stale) ids.
   */
  addStakesByPair(pairs: Array<{ stakeId: number; eventId: number }>, source?: Suggestion): PostOutcome | null {
    return this.postLegs(pairs.map((p) => ({ ...p, factor: NaN,
      isLive: source?.legs?.find((l) => Number(l.event_id) === p.eventId)?.is_live ?? source?.is_live,
    })));
  }

  private postLegs(rawLegs: Array<{ stakeId: number; eventId: number; factor: number; isLive?: boolean }>, suggestionIsLive?: boolean): PostOutcome | null {
    try {
      const sf = (window as unknown as DigitainWindow).SportFrame as
        | Record<string, unknown>
        | undefined;
      if (!sf) return null;

      // TWO TRANSPORTS, one per boot mode — the same split DigitainSource already
      // documents for the event bus:
      //
      //  - iframe (Hannibalbet): the sportsbook lives in a cross-origin iframe, so
      //    the app-event goes to `SportFrame.elem.contentWindow`.
      //  - native (Parikara): the sportsbook renders in OUR document — there IS no
      //    sportsbook iframe (KB §"Parikara is native DOM"), so `SportFrame.elem` is
      //    undefined. The app is listening on the parent window, so post there.
      //
      // The native path is LIVE-VERIFIED on Parikara (2026-07-30): singles and
      // multi-leg combos both land in the real slip via the parent window, confirmed
      // by the operator's own `/Betting/AddStake` ack. It cannot regress Hannibalbet
      // — the iframe branch is unchanged and takes priority — and the navigate
      // fallback still catches the case where nothing is listening.
      const elem = sf["elem"] as { contentWindow?: Window } | undefined;
      const iframeCw = elem?.contentWindow;
      let cw: Window;
      if (iframeCw && typeof iframeCw.postMessage === "function") {
        cw = iframeCw;                                  // iframe mode — unchanged
      } else if (!hasSportsbookIframe() && typeof window.postMessage === "function") {
        cw = window;                                    // native mode — post to the parent
      } else {
        // An iframe sportsbook EXISTS but we can't reach its contentWindow. Do NOT
        // post to the parent: nothing there is listening, and returning true would
        // tell the user "Sent to betslip" when the bet never landed. Report failure
        // so the caller falls back to navigate — an honest outcome beats a false one.
        return null;
      }

      const legs = rawLegs.filter(
        (x) => Number.isFinite(x.stakeId) && x.stakeId > 0 && Number.isFinite(x.eventId) && x.eventId > 0,
      );
      if (legs.length === 0) return null;

      const type = this.cfg.toggle_stake_type || "toggleStake";
      const sender = this.cfg.app_event_sender || "bootstrapper";
      // TWO lists, and the split is safety-critical. `posted` is what THIS call
      // sent; `alreadyIn` is what an earlier add already owns. The caller may only
      // mutate the former — retracting a leg from an earlier add toggles a
      // selection the user deliberately made straight OUT of their coupon.
      const postedNow: StakePair[] = [];
      const alreadyIn: StakePair[] = [];
      for (const leg of legs) {
        const key = `${leg.eventId}:${leg.stakeId}`;
        // Skip if the leg is confirmed in the slip OR still awaiting an answer.
        // Re-posting either one risks toggling OFF a leg that did land. Only
        // forget() — called for legs the operator says never landed — reopens a
        // leg for re-posting.
        if (this.added.has(key) || this.posted.has(key)) {
          const shared = { stakeId: leg.stakeId, eventId: leg.eventId };
          this.sharedPairs.add(shared);
          alreadyIn.push(shared);
          continue;
        }
        const pair = { stakeId: leg.stakeId, eventId: leg.eventId };
        postedNow.push(pair);
        cw.postMessage({
          type,
          sender,
          data: {
            stakeId: leg.stakeId,
            eventId: leg.eventId,
            // ⛔ The LEG's own flag first (see addStakes), then the card's, then
            // live — the measured per-event answer inside `legIsLive` still wins
            // over both, because a fixture can kick off while the card sits open.
            matchIsLive: this.legIsLive(leg.eventId, leg.isLive ?? suggestionIsLive),
            isSuperTip: false,
            promoId: 0,
            cardId: null,
            ...(Number.isFinite(leg.factor) && leg.factor > 0 ? { factor: leg.factor } : {}),
          },
        }, "*");
        this.posted.add(key);
        const owner = ++this.nextOwnership;
        this.ownership.set(key, owner);
        this.pairOwnership.set(pair, owner);
      }
      // Deliberately NOT force-opening the betslip panel: the toggles update the
      // slip-bar count natively, and our openBetslip on top of that made the panel
      // jolt open/collapse over the page ("opens and wraps suddenly" — live soak
      // 2026-07-07). Feedback = slip-bar count + highlighted odds + "✓ In betslip";
      // the user opens the slip when they're ready.
      return { posted: postedNow, alreadyIn };
    } catch {
      return null;
    }
  }

  /**
   * Toggle specific stakes back OUT of the slip.
   *
   * Needed because the ack-based add gate posts FIRST and confirms after: if only
   * some legs of a combo land, the user would be left holding a partial acca they
   * never asked for. `toggleStake` is symmetric, so re-posting removes. Only the
   * pairs we actually marked as added are touched — re-posting a leg that never
   * landed would ADD it, which is the exact bug this prevents.
   */
  retractStakes(pairs: Array<{ stakeId: number; eventId: number }>, source?: Suggestion): void {
    try {
      const sf = (window as unknown as DigitainWindow).SportFrame as
        | Record<string, unknown>
        | undefined;
      if (!sf) return;
      const elem = sf["elem"] as { contentWindow?: Window } | undefined;
      const iframeCw = elem?.contentWindow;
      let cw: Window;
      if (iframeCw && typeof iframeCw.postMessage === "function") cw = iframeCw;
      else if (!hasSportsbookIframe() && typeof window.postMessage === "function") cw = window;
      else return;

      const type = this.cfg.toggle_stake_type || "toggleStake";
      const sender = this.cfg.app_event_sender || "bootstrapper";
      for (const p of pairs) {
        const key = `${p.eventId}:${p.stakeId}`;
        if (!this.added.has(key)) continue; // never landed — toggling would ADD it
        if (!this.ownsLeg(p)) continue;
        cw.postMessage({
          type,
          sender,
          data: {
            stakeId: p.stakeId,
            eventId: p.eventId,
            matchIsLive: this.legIsLive(p.eventId,
              source?.legs?.find((l) => Number(l.event_id) === p.eventId)?.is_live ?? source?.is_live),
            isSuperTip: false,
            promoId: 0,
            cardId: null,
          },
        }, "*");
        this.added.delete(key);
        this.ownership.delete(key);
      }
    } catch {
      /* best-effort cleanup */
    }
  }

  /**
   * Toggle DEAD legs back out of the coupon.
   *
   * Separate from `retractStakes` because that one gates on `added`, and a dead leg
   * never reaches `added` — `landedStakes` (correctly) refuses to count it. The
   * gate exists because `toggleStake` is symmetric, so toggling a leg that is NOT
   * in the coupon ADDS it; the licence to skip it here is that the operator's own
   * `CheckedStakesList` named these legs as present. That is the same class of
   * evidence `added` is built from — the operator's answer, not our hope — so the
   * invariant the gate protects is upheld, not bypassed.
   *
   * Live-verified on Parikara 2026-08-13: re-posting `toggleStake` for a leg the
   * coupon held with `Deleted: true` fired `/Betting/RemoveStake` and emptied the
   * slip. Nothing was added back.
   */
  removeDeadStakes(pairs: Array<{ stakeId: number; eventId: number }>, source?: Suggestion): void {
    try {
      const sf = (window as unknown as DigitainWindow).SportFrame as
        | Record<string, unknown>
        | undefined;
      if (!sf) return;
      const elem = sf["elem"] as { contentWindow?: Window } | undefined;
      const iframeCw = elem?.contentWindow;
      let cw: Window;
      if (iframeCw && typeof iframeCw.postMessage === "function") cw = iframeCw;
      else if (!hasSportsbookIframe() && typeof window.postMessage === "function") cw = window;
      else return;

      const type = this.cfg.toggle_stake_type || "toggleStake";
      const sender = this.cfg.app_event_sender || "bootstrapper";
      for (const p of pairs) {
        const owner = this.pairOwnership.get(p);
        const key = `${p.eventId}:${p.stakeId}`;
        if (owner != null && owner !== this.ownership.get(key)) continue;
        cw.postMessage({
          type,
          sender,
          data: {
            stakeId: p.stakeId,
            eventId: p.eventId,
            matchIsLive: this.legIsLive(p.eventId,
              source?.legs?.find((l) => Number(l.event_id) === p.eventId)?.is_live ?? source?.is_live),
            isSuperTip: false,
            promoId: 0,
            cardId: null,
          },
        }, "*");
        this.posted.delete(key);
        this.added.delete(key);
        this.ownership.delete(key);
      }
    } catch {
      /* best-effort cleanup */
    }
  }

  /**
   * Inject a freshly-minted booking number (for the ACTUAL recommended selections)
   * into the betslip. Safe: unlike the removed hardcoded-demo-number hazard, this
   * number is minted on the fly from our own stakes (see DigitainFeedSource.mintBooking).
   * Fires the sportsbook's "Booked Bets → Load" resolve. Returns true if dispatched.
   */
  injectBooking(bookNumber: number): boolean {
    try {
      const sf = (window as unknown as DigitainWindow).SportFrame as
        | Record<string, (...args: unknown[]) => void>
        | undefined;
      if (!sf) return false;
      const inject = sf[this.cfg.coupon_method];
      if (typeof inject !== "function") return false;
      inject.call(sf, bookNumber);
      const open = sf[this.cfg.open_betslip_method];
      if (typeof open === "function") open.call(sf, undefined);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Open a specific event in the sportsbook on Parikara.
   *
   * `SportFrame.openEvent` cannot do this here: every SportFrame command routes
   * through `sendMessage`, which posts to `elem.contentWindow`, and Parikara has no
   * sportsbook iframe — `elem` is null and the call is a silent no-op. That is what
   * made "open the match" unreachable, and the reason `add()` below returns null.
   *
   * But the board is not unreachable, only the iframe transport is. Parikara renders
   * the sportsbook in OUR document as a React app that publishes itself on
   * `window.sportdigi.mobile`, and its router is an in-memory history — which is why
   * the address bar never changes and why there is no href to follow. Its own betslip
   * navigates by pushing onto that history; nothing else is involved (the
   * `navigateToEventPage` message it emits afterwards is a notification to the
   * wrapper, and posting that message back at the app does nothing).
   *
   * So we do exactly what their coupon does. Live-verified on Parikara 2026-08-13
   * across three different matches; `event` + `sport` + `live` is the minimal query.
   *
   * Reached through the React fiber, so a Digitain bundle upgrade can break it. It
   * fails CLOSED — null, never a cheerful lie — because the CTA renders "ОТКРЫТО У
   * БУКМЕКЕРА" off this answer and an optimistic id here is a claim on screen over a
   * page that never moved (owner-reported 2026-08-03).
   */
  private openEventNative(eventId: string, sportId: number | null, isLive: boolean): boolean {
    // The route was only ever verified carrying the sport id, and a push() always
    // "succeeds" whether or not it resolves — so a missing sport id cannot be
    // papered over with a hopeful navigation. Refuse instead; the caller then
    // reports no navigation rather than printing "ОТКРЫТО У БУКМЕКЕРА" over a page
    // that may not have moved.
    if (sportId == null) return false;
    try {
      const host = document.querySelector("sport-mobile");
      const root = host && (host as Element & { shadowRoot?: ShadowRoot | null }).shadowRoot;
      if (!root) return false;
      const history = findRouterHistory(root);
      if (!history) return false;
      // A router push to its current match is a no-op, not a new navigation.
      // The title can outlive the route change while the peek holds its card.
      if (history.location.pathname === "/event-details"
        && new URLSearchParams(history.location.search ?? "").get("event") === String(eventId)) return false;

      const q = `event=${encodeURIComponent(eventId)}`
        + `&sport=${encodeURIComponent(String(sportId))}`
        + `&live=${isLive ? 1 : 0}`;
      history.push(`/event-details?${q}`);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Can this operator surface be navigated to a match at all?
   *
   * Asked BEFORE the card renders its "open the match" control, because a control
   * that does nothing is worse than no control — and on an operator where every
   * navigation route is dead, doing nothing is exactly what it would do.
   */
  canOpenMatch(): boolean {
    try {
      const sf = (window as unknown as DigitainWindow).SportFrame as
        | Record<string, unknown>
        | undefined;
      if (!sf) return false;
      // iframe operators (Hannibalbet) route through SportFrame.openEvent.
      if ((sf as { elem?: unknown }).elem) return typeof sf["openEvent"] === "function";
      // native operators (Parikara): the sport app must be in this document.
      const host = document.querySelector("sport-mobile");
      const root = host && (host as Element & { shadowRoot?: ShadowRoot | null }).shadowRoot;
      // A mounted native app alone is not a route: on Parikara the router can be
      // absent while its shell is already visible. `openEventNative` needs this
      // history object too, so do not render a dead "Open match" control.
      return !!findRouterHistory(root ?? null);
    } catch {
      return false;
    }
  }

  /**
   * Take the user to a specific match — the USER-FACING action, reproducing what
   * Digitain's own betslip does when its coupon row is tapped.
   *
   * Distinct from `add()`, which navigates only as a last resort after a failed
   * betslip insert. That distinction was the defect: the navigation existed but
   * nothing the user could press ever reached it.
   *
   * Returns whether the sportsbook actually moved, so the caller reports honestly
   * and only a real navigation is recorded as influence.
   */
  openMatch(eventId: string, sportId: number | null, isLive: boolean): boolean {
    try {
      const sf = (window as unknown as DigitainWindow).SportFrame as
        | Record<string, unknown>
        | undefined;
      if (!sf || !eventId) return false;
      if (!(sf as { elem?: unknown }).elem) {
        return this.openEventNative(String(eventId), sportId, isLive);
      }
      const openEvent = sf["openEvent"];
      if (typeof openEvent !== "function") return false;
      try {
        (openEvent as (...a: unknown[]) => void).call(sf, Number(eventId) || eventId);
      } catch {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  add(suggestion: Suggestion): string | null {
    // Navigate fallback — used only when minting a booking number fails (see
    // injectBooking + DigitainFeedSource.mintBooking for the real add-to-betslip).
    // We never inject a hardcoded/stale coupon here (that was the removed hazard):
    // this only navigates the sportsbook to the pick so the user can tap the odds.
    //
    // RETURN CONTRACT: the target event id ONLY when the navigate was actually
    // delivered; `null` whenever nothing happened. The caller renders a user-facing
    // claim off this value, so an optimistic id here is a lie on screen — it is
    // exactly how "ОТКРЫТО У БУКМЕКЕРА" came to sit over a page that never moved
    // (owner-reported on Parikara, 2026-08-03). When in doubt, return null.
    try {
      const sf = (window as unknown as DigitainWindow).SportFrame as
        | Record<string, (...args: unknown[]) => void>
        | undefined;
      if (!sf) return null;

      // Navigate to the target event. For a combo, take the user to the first leg
      // so they can start building the acca (no single event id for the acca).
      const targetEventId = (suggestion.legs && suggestion.legs.length)
        ? suggestion.legs[0].event_id
        : suggestion.event_id;
      if (!targetEventId) return null;

      // `openEvent` delivers through `SportFrame.sendMessage`, which posts to
      // `elem.contentWindow`. Parikara renders Digitain as NATIVE DOM — its board
      // lives in the OPEN shadow root of <sport-mobile>, there is no sportsbook
      // iframe, and `elem` is null. sendMessage guards internally, so the call
      // neither navigates NOR throws: it is a completely silent no-op.
      // Live-verified on parikara.com 2026-08-03 (elem === null, openEvent is a
      // real function, page did not move, nothing thrown).
      //
      // Without this check that no-op reads as a successful navigation, which is
      // the whole defect. openPage/setSportAppRoute/openBetslip all route through
      // the same sendMessage, and `navigateToEvent` on the bootstrapper bus is an
      // app->wrapper notification that ignores inbound posts — so every SportFrame
      // route is genuinely dead here.
      //
      // The BOARD is not, though: drive the sport app's own router directly. This
      // used to `return null` and that was the whole of "Add to betslip silently
      // degrades to nothing" on Parikara.
      if (!(sf as unknown as { elem?: unknown }).elem) {
        const sportId = typeof suggestion.sport_id === "number" ? suggestion.sport_id : null;
        const isLive = suggestion.is_live !== false;
        return this.openEventNative(String(targetEventId), sportId, isLive)
          ? String(targetEventId)
          : null;
      }

      const openEvent = sf["openEvent"];
      if (typeof openEvent === "function") {
        try {
          openEvent.call(sf, Number(targetEventId) || targetEventId);
        } catch {
          return null; // it threw — nothing navigated, and we must not claim it did
        }
        // No openBetslip here either: the navigate fallback adds nothing to the
        // slip, so popping the panel (with a possibly-drifted old coupon and its
        // "Availability changed" prompt) over the freshly-navigated pick was pure
        // noise — the jolt the operator soak flagged.
        return targetEventId;
      }
      return null;
    } catch {
      // best-effort — attribution still logged by caller
      return null;
    }
  }
}

// ---------------------------------------------------------------------------
// DigitainCatalog — the candidate pool.
//
// The event bus only exposes the current event, so to recommend OTHER live
// matches we boot a HIDDEN Digitain LiveEventWidget (<sport-live>) via
// Bootstrapper.bootLandingWidgets and harvest its events. The widget is a React
// app; events live in the `__reactProps$*` tree as objects shaped
// {Id, HT, AT, SId, SN, CHId, CN, ...} (verified live 2026-07-04). This React
// traversal is the only clean read path; it's isolated here so a Digitain widget
// change degrades gracefully (empty pool → SDK falls back to current match).
// ---------------------------------------------------------------------------

const CATALOG_CONTAINER_ID = "bc-digitain-catalog";

// Parse a Digitain event's `StakeType` array into normalized markets.
// StakeType = [{Id, N, Stakes:[{Id, SN, F}]}] where F is odds ×100 (150 → 1.50).
function parseMarkets(stakeType: unknown): Market[] {
  if (!Array.isArray(stakeType)) return [];
  const markets: Market[] = [];
  for (const m of stakeType) {
    if (!m || typeof m !== "object") continue;
    const mm = m as Record<string, unknown>;
    const stakes = Array.isArray(mm.Stakes) ? mm.Stakes : [];
    const selections = [];
    const suspended: string[] = [];
    for (const s of stakes) {
      if (!s || typeof s !== "object") continue;
      const ss = s as Record<string, unknown>;
      if (ss.Id == null) continue;
      const f = Number(ss.F);
      // An unpriced stake is REMEMBERED, not dropped. Digitain's own betslip shows
      // such a leg dimmed under "Доступность событий изменилась", so the state is
      // real and user-visible — we were simply throwing away the only field we
      // already read that could carry it. It stays out of `selections`, so it can
      // never be ranked or recommended.
      if (!Number.isFinite(f) || f <= 0) {
        suspended.push(String(ss.Id));
        continue;
      }
      selections.push({
        stake_id: String(ss.Id),
        // ⛔ `N` WHEN THERE IS NO `SN`. The app STORE's stakes carry the side in
        // `N`; reading `SN` alone sent «Меньше» 3.5 out as selection "" (seen in a
        // real tab 2026-10-02), and the advice then argued the other side.
        name: String(ss.SN || ss.N || ""),
        odds: Math.round(f * 100) / 100, // F is decimal odds directly (live-verified)
        // Carried so a rotated stake id can be re-resolved to the SAME outcome —
        // see MarketSelection.code. Both are present on every live stake in the
        // feed (verified on the Parikara wire 2026-08-13).
        code: Number.isFinite(Number(ss.SC)) ? Number(ss.SC) : null,
        argument: ss.A == null ? null : Number(ss.A),
      });
    }
    if (selections.length > 0 || suspended.length > 0) {
      markets.push({
        id: String(mm.Id ?? ""),
        name: String(mm.N ?? ""),
        selections,
        ...(suspended.length ? { suspended } : {}),
      });
    }
  }
  return markets;
}

// Match-winner market ids (mirror of the backend's _MATCH_RESULT_MARKET_IDS):
// StakeType id 1 = 3-way 1X2, 702 = 2-way moneyline.
const WINNER_MARKET_IDS = new Set(["1", "702"]);
const WIN_CODES = /^(win1|win2|w1|w2|1|2|п1|п2)$/;

/** True when the match still has BOTH sides of its winner market priced. When a
 * team runs away with it the book removes the leading line — the backend then
 * (rightly) refuses to nominate a favorite and the pick degrades to a "watch"
 * card. Detecting that here lets the pool keep real picks ahead of frozen ones. */
function hasBettableWinnerPair(m: LiveMatch): boolean {
  const markets = m.markets;
  if (!markets || markets.length === 0) return true; // React-harvest events carry no markets — don't judge them
  const winner = markets.find((mk) => WINNER_MARKET_IDS.has(String(mk.id)));
  if (!winner) return false;
  // Same acceptance as the backend's _pick_favorite: Win1/Win2-style codes OR the
  // team names themselves (some feeds label selections with the team name).
  const home = (m.home || "").trim().toLowerCase();
  const away = (m.away || "").trim().toLowerCase();
  let sides = 0;
  for (const s of winner.selections) {
    const name = s.name.trim().toLowerCase();
    if ((WIN_CODES.test(name) || name === home || name === away) && s.odds > 1) sides++;
  }
  return sides >= 2;
}

// Walk a rendered widget root's React props for event objects → CatalogEvents.
// Exported for unit testing with a mock root.
export function harvestReactEvents(root: {
  querySelectorAll: (sel: string) => ArrayLike<unknown>;
}): LiveMatch[] {
  const found = new Map<string, LiveMatch>();
  const visited = new Set<unknown>();
  // Odds books (StakeType, ~40+ markets/event) live deep in the widget's React
  // tree, so give the walk enough budget to reach them past the summary nodes.
  let budget = 60000;

  const walk = (node: unknown): void => {
    if (budget-- <= 0 || node === null || typeof node !== "object" || visited.has(node)) return;
    visited.add(node);
    const n = node as Record<string, unknown>;
    if (n.Id != null && n.HT !== undefined && n.AT !== undefined) {
      const id = String(n.Id);
      const markets = parseMarkets(n.StakeType);
      const scored_lines = scoredMainLines(markets);
      const existing = found.get(id);
      if (!existing) {
        const hs = n.HS, as = n.AS;
        found.set(id, {
          id,
          // Localized for display, English for matching — see mapApiEvent.
          sport: String(n.SN ?? n.ESN ?? ""),
          league: String(n.CN ?? n.ECN ?? ""),
          sport_en: String(n.ESN ?? n.SN ?? ""),
          league_en: String(n.ECN ?? n.CN ?? ""),
          country_en: String(n.ECtN ?? ""),
          sport_id: typeof n.SId === "number" ? n.SId : null,
          champ_id: typeof n.CHId === "number" ? n.CHId : null,
          home: String(n.HT ?? ""),
          away: String(n.AT ?? ""),
          score: hs != null && as != null ? `${hs}-${as}` : "",
          minute: 0,
          clock: extractClock(n as Record<string, unknown>),
          status: "live", // LiveEventWidget yields live events only
          markets,
          scored_lines,
          // ⭐ The kick-off time, when the harvested node carries one.
          //
          // ⛔ THIS MAPPER IS A SIBLING OF `mapApiEvent` AND DOES NOT CALL IT.
          // The API path and this React-harvest path build a LiveMatch from the
          // same node shape in two places, so a field added to one is silently
          // absent from the other — and this is the path the native-DOM
          // operators (Parikara) actually use, i.e. the one that matters for
          // `event_catalog.starts_at`. Adding `starts_at` to `mapApiEvent`
          // alone changed nothing in production. Look for the sibling.
          ...(parsePrematchStart(n) ? { starts_at: parsePrematchStart(n) as string } : {}),
        });
      } else if ((!existing.markets || existing.markets.length === 0) && markets.length > 0) {
        // The widget renders a lightweight summary node (teams/score, no odds)
        // AND a full node carrying the StakeType book. Whichever is walked first
        // wins the identity; upgrade its markets when the richer node turns up.
        existing.markets = markets;
        existing.scored_lines = scored_lines;
      }
    }
    if (Array.isArray(node)) {
      for (let i = 0; i < node.length; i++) walk(node[i]);
      return;
    }
    for (const k in n) {
      // skip React fiber internals to avoid huge/cyclic graphs
      if (k === "ref" || k === "_owner" || k === "_store" || k === "stateNode") continue;
      try {
        const v = n[k];
        if (v && typeof v === "object") walk(v);
      } catch { /* getter threw */ }
    }
  };

  const els = root.querySelectorAll("div");
  for (let i = 0; i < els.length; i++) {
    const el = els[i] as Record<string, unknown>;
    for (const key in el) {
      if (key.indexOf("__reactProps") === 0) {
        try { walk(el[key]); } catch { /* ignore */ }
      }
    }
  }
  return [...found.values()];
}

// Match-result market ids we keep: 1 = 3-way 1X2, 702 = 2-way moneyline. The
// full-feed pull returns every market (~84/event); we only need the winner market
// for a favorite, so trim to these — a 52-event pool must stay a small request body
// (the /suggestions call has a 3s abort; a multi-MB body blows it).
const MATCH_RESULT_MARKET_IDS = new Set(["1", "702"]);

// ── The match clock (captured live off parikara.com, 2026-08-16) ────────────
//
// Digitain's live-event node carries a clock. It was never read: `minute` was
// hardcoded 0 in both feed paths, and a native RU read complained that "trailing
// by 6 points" is meaningless without knowing how long is left.
//
// ⛔⛔ `PT` MEANS A DIFFERENT THING IN EVERY SPORT. Verified twice, ~2 minutes
// apart, against the operator's OWN rendering:
//
//   Football    PT 90, PS 8, MEM 92  → UI "2-й тайм 92:08". MEM counts UP.
//                                      PT is the regulation LENGTH, not a clock.
//   Basketball  PT 8,  PS 0, MEM 0   → UI "4-я четверть 06' 00"" 2 min later.
//                                      Counts DOWN: minutes REMAINING in period.
//   Ice Hockey  PT 24, PS 2, MEM 0   → UI "3-й период 26'" 2 min later.
//                                      Counts UP: TOTAL elapsed minutes.
//   Tennis / Table Tennis            → PT/PS/MEM all 0. Untimed. No clock exists.
//
// Reading basketball's PT as elapsed would say "8 minutes played" when 8 minutes
// REMAIN — a confidently inverted claim, the same failure class as the RU
// «рынок сокращает его шансы» defect. Worse than passing nothing at all.
//
// Hence: an explicit allowlist keyed on the LANGUAGE-INDEPENDENT `SId`, never on
// a localized sport name. A sport whose semantics have not been verified on a
// real board gets NO clock and keeps today's behaviour. ⛔ Do not add an entry
// from a guess — capture the sport on a live board and watch the value move.
type ClockKind = "elapsed" | "remaining_in_period";
const CLOCK_BY_SPORT_ID: Record<number, ClockKind> = {
  1: "elapsed", // Football   — from MEM
  4: "remaining_in_period", // Basketball — from PT/PS, counts down
  10: "elapsed", // Ice Hockey — from PT, counts up (total)
};

/** Extract a normalized match clock, or null when this sport is unverified or
 *  untimed. Never guesses: an allowlist miss returns null. */
export function extractClock(n: Record<string, unknown>): {
  kind: ClockKind;
  minutes: number;
  period: number | null;
  period_name: string;
} | null {
  const sid = typeof n.SId === "number" ? n.SId : -1;
  const kind = CLOCK_BY_SPORT_ID[sid];
  if (!kind) return null;
  const num = (v: unknown) => (typeof v === "number" && isFinite(v) ? v : null);
  // Football's elapsed minutes live in MEM; hockey's in PT. Both count UP, so a
  // 0 is indistinguishable from "not started" — treat it as no clock rather than
  // asserting minute zero.
  let minutes: number | null = null;
  if (kind === "elapsed") minutes = num(n.MEM) || num(n.PT);
  else minutes = num(n.PT);
  if (minutes == null || minutes <= 0) return null;
  const period = num(n.CP);
  return {
    kind,
    minutes,
    period: period && period > 0 ? period : null,
    // Localized on purpose — this is DISPLAY text («4-я четверть»), and Digitain
    // sends no English period name (EPN/PN were empty on every node captured).
    period_name: String(n.ES ?? n.ShortStatus ?? ""),
  };
}

// Map a raw /Live/GetTopLiveEvents API event object to a LiveMatch (winner market only).
export function mapApiEvent(e: unknown): LiveMatch | null {
  if (e == null || typeof e !== "object") return null;
  const n = e as Record<string, unknown>;
  if (n.Id == null || n.HT === undefined || n.AT === undefined) return null;
  const hs = n.HS, as = n.AS;
  const allMarkets = parseMarkets(n.StakeType);
  const markets = allMarkets.filter((m) => MATCH_RESULT_MARKET_IDS.has(String(m.id)));
  const scored_lines = scoredMainLines(allMarkets);
  const startsAt = parsePrematchStart(n);
  return {
    id: String(n.Id),
    // Digitain sends BOTH names on every event (verified live 2026-07-13):
    //   SN / CN   — in the USER'S language (the request template carries their langId)
    //   ESN / ECN / ECtN — ALWAYS English, whatever the locale
    // So the localized pair is what we DISPLAY, and the English pair is what we
    // MATCH on. That's what keeps `leagues.json` keywords, the flag map and the
    // virtual/e-sport exclusion working in French and Arabic without translating
    // a single keyword — on an Arabic board `CN` is "أيسلندا. الدوري الممتاز"
    // while `ECN` is still "Iceland. Premier League".
    sport: String(n.SN ?? n.ESN ?? ""),
    league: String(n.CN ?? n.ECN ?? ""),
    sport_en: String(n.ESN ?? n.SN ?? ""),
    league_en: String(n.ECN ?? n.CN ?? ""),
    country_en: String(n.ECtN ?? ""),
    sport_id: typeof n.SId === "number" ? n.SId : null,
    champ_id: typeof n.CHId === "number" ? n.CHId : null,
    home: String(n.HT ?? ""),
    away: String(n.AT ?? ""),
    // Only when the feed really sent them — see LiveMatch.home_en. The live
    // board sends neither; the pre-match board sends both, and mapPrematchEvent
    // reaches this same mapper.
    ...(n.EHT ? { home_en: String(n.EHT) } : {}),
    ...(n.EAT ? { away_en: String(n.EAT) } : {}),
    // NO `home_en`/`away_en` here, and that is a measurement, not an omission:
    // 97 live events on the Parikara board (2026-08-09) carried HT/AT and ZERO
    // carried EHT/EAT. Digitain sends the English pair for SPORT and LEAGUE on the
    // feed, and for TEAMS only in the account payloads (`GetAllFavoriteEvents`,
    // `GetBetHistoryItemDetails`). A field that is always "" is worse than absent —
    // it reads as coverage. Team matching therefore runs localized↔localized, which
    // is sound because both sides come from this same feed under the same langId.
    score: hs != null && as != null ? `${hs}-${as}` : "",
    // `minute` stays 0: it is the legacy flat field the mobile chip renders and
    // it cannot express basketball's count-DOWN clock without lying. The honest,
    // sport-aware value is `clock` — see extractClock's header comment.
    minute: 0,
    clock: extractClock(n),
    status: "live", // GetTopLiveEvents yields live events only
    markets,
    scored_lines,
    // `IsLS` = has a live-TV stream (verified: true for the match on the LIVE TV
    // player; ~7/60 of the board). Powers the "📺 Live on TV" context chip + boost.
    has_stream: n.IsLS === true,
    // ⭐ KICK-OFF TIME ON A LIVE EVENT TOO, when the feed sends one.
    //
    // ⛔ THIS MAPPER USED TO DROP `D`, and the comment on `mapPrematchEvent`
    // asserted that a live event "has a kick-off time that no live event
    // carries". That was an assumption, never a measurement, and it is the
    // reason `event_catalog.starts_at` is NULL on most rows: the pre-match board
    // is the only thing that ever supplied it, and the pre-match board is pulled
    // by a small share of sessions. Everything downstream of the catalog that
    // wants a date — the demand export, and any fact-source join narrowed by
    // one — inherits that hole.
    //
    // Guarded by presence, not by status: a feed that omits `D` on live events
    // yields `null` and the key is simply absent, exactly as before. A feed that
    // sends it now gets recorded.
    //
    // ⚠️ This does NOT make a live event rank as upcoming. `_compute_liveness`
    // tests `is_live` FIRST and returns the live rung, and the countdown branch
    // in `/suggestions` skips any candidate with `is_live` set. `status` stays
    // "live" and remains what the backend derives `is_live` from.
    ...(startsAt ? { starts_at: startsAt } : {}),
  };
}

/** The pre-match board's kick-off field → an ISO-8601 UTC instant, or null.
 *
 * `D` is an ISO-8601 string with an explicit `Z`, verified on a real board
 * 2026-08-28 (`product/docs/digitain/prematch-board-capture.md`) — not epoch
 * seconds, not epoch milliseconds, not a local-time string. ⛔ It was
 * cross-checked against three fixtures' real kick-offs, NOT against the clock
 * the operator's UI renders: that one converts to the VIEWER's timezone, so a
 * board read off the screen looks hours out and the error is invisible — every
 * card still renders, it is just wrong about when the match starts.
 *
 * Numbers are still handled, because a units mistake is the failure mode with no
 * symptom: if the field ever arrives numeric, seconds and milliseconds are
 * separated by magnitude rather than assumed.
 *
 * ⚠️ A string with NO offset is read as UTC, not as browser-local — which is
 * what `new Date("2026-08-28T19:00:00")` would do. Every `D` seen on a real
 * board is `Z`-suffixed, so this branch is unreachable today; it exists because
 * the server's `_minutes_to_start` reads a naive timestamp as UTC
 * (`when.replace(tzinfo=timezone.utc)`), and the two ends disagreeing about the
 * same string would put every fixture hours off with nothing to show for it.
 */
function parsePrematchStart(n: Record<string, unknown>): string | null {
  const raw = n.D;
  if (raw == null) return null;
  let d: Date;
  if (typeof raw === "number") {
    d = new Date(raw < 1e12 ? raw * 1000 : raw);
  } else {
    const str = String(raw).trim();
    // ISO-8601 date-time carrying neither `Z` nor a ±HH:MM offset → stamp UTC.
    const naive = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(str);
    d = new Date(naive ? str.replace(" ", "T") + "Z" : str);
  }
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Map a raw pre-match board event to a LiveMatch.
 *
 * ⛔ Deliberately NOT a relaxation of `mapApiEvent`. That function hardcodes
 * `status: "live"` because `GetTopLiveEvents` yields live events only, and that
 * constant is load-bearing elsewhere — the backend derives `is_live` from it.
 * A pre-match event has no score, no clock and no elapsed minute, and its
 * kick-off time is REQUIRED — an event with no usable `D` is not a pre-match
 * candidate at all, which is the one thing that stays specific to this mapper.
 * ⛔ The kick-off time itself is NOT exclusive to pre-match: `mapApiEvent` reads
 * `D` too, when the feed sends it. The old wording here claimed "no live event
 * carries" one and that claim was never measured.
 *
 * Everything that is NOT live-specific — the ECN/ESN English-name pair that the
 * ranker, the flag map and the e-sport gate all match on, the market parse — is
 * shared with `mapApiEvent` rather than duplicated.
 *
 * ⚠️ The one field that is NOT shared: the pre-match board sends its markets
 * under `StakeTypeS`, plural, where the live board sends `StakeType`. Nothing
 * fails loudly when that is missed — the card renders with an empty market and
 * simply cannot be priced — so the plural is normalized here, once, and asserted
 * in `adapter.test.ts`.
 */
export function mapPrematchEvent(n: Record<string, unknown>): LiveMatch | null {
  if (n == null || typeof n !== "object") return null;
  const startsAt = parsePrematchStart(n);
  if (!startsAt) return null; // no usable kick-off time: not a pre-match candidate
  const base = mapApiEvent(
    "StakeTypes" in n && !("StakeType" in n) ? { ...n, StakeType: n.StakeTypes } : n,
  );
  if (!base) return null;
  return {
    ...base,
    status: "prematch",
    // A pre-match event carries HS/AS null, PT/PS/MEM 0 and ES "" — stated here
    // rather than inherited, so a future feed that starts sending a placeholder
    // score cannot leak "0-0" onto a card for a match that has not kicked off.
    score: "",
    minute: 0,
    clock: null,
    starts_at: startsAt,
  };
}

// DigitainFeedSource — the REAL odds source.
//
// Digitain embeds a cross-origin `.../Tools/RequestHelper` iframe that fetches the
// Digitain HTTP API and relays each response to the parent via postMessage
// (`{sender:"http-api-response", url, res}`). We capture those responses passively
// in the parent — no WS hook, no cross-origin injection, no React scraping. The
// `/Live/GetTopLiveEvents` payload carries the full odds book (StakeType) per event.
// (docs/digitain-ws-capture-plan.md — Phase 1 found the transport is this proxy, not a WS.)
const DIGITAIN_IFRAME_HINT = "RequestHelper";
const FULLFEED_INTERVAL_MS = 45000; // re-pull the whole board every 45s
const FULLFEED_EVENT_TTL_MS = 100000; // drop events not re-seen in ~2 cycles
const FULLFEED_COUNT = 80; // per-sport cap (>80 observed empty; 80 verified OK)
// Simulated/virtual sports we don't recommend (they'd dominate by volume).
// "e-?sport" (not just "^e-"): Digitain names the sport "Esports" — no dash — and its
// CS2 favorites are noisy (seen live: CS2 legs blended into a "World Cup" acca).
export const EXCLUDE_SPORT_NAME = /virtual|^e-|e-?sport/i;
// PRE-MATCH pull constants (captured live 2026-08-28 — see
// product/docs/digitain/prematch-board-capture.md).
// `applicationType: 3` is what the operator's own web app sends.
const PREMATCH_APPLICATION_TYPE = 3;
// The operator-authored top-tier group, matched on its ALWAYS-ENGLISH name.
const PREMATCH_POPULAR_GROUP = /popular/i;
// One request per tournament against the OPERATOR'S production API, so the
// fan-out is bounded. Parikara's popular group held 8 on capture day.
const PREMATCH_MAX_TOURNAMENTS = 16;
// The last-hour pass (fetchPrematchBoard, "By-period chain" in the capture doc).
// 60 minutes with `period` 5 is EXACTLY what the operator's «1 час» chip sends —
// replayed as captured, not a guessed combination. The board refreshes every 5
// minutes and the server keeps a fixture until kickoff, so it lands by T-60..55.
export const PREMATCH_SOON_MINUTES = 60;
export const PREMATCH_SOON_PERIOD = 5;
// Football, tennis, basketball (Digitain sport ids, GetTopLiveSports 2026-09-25):
// the only sports the fact layer covers, and NO FACTS = NO CARD — pulling any
// other sport spends the operator's API for fixtures that can never render.
export const PREMATCH_SOON_SPORT_IDS = [1, 3, 4];
// Its own cap, separate from `prematch_tournaments`: at 19:13 UTC on a Friday
// football's last hour held 5 regions / 6 fixtures. Each tournament is one
// events request against the operator's production API.
export const PREMATCH_SOON_MAX_TOURNAMENTS = 30;
// The operator/geo triple every replayed request must carry unchanged: which
// language the board comes back in, who the partner is, and what geo the
// operator decided this session is in. Never invented, only inherited.
const AUTH_PARAM_KEYS = new Set(["langid", "partnerid", "countrycode"]);

// DigitainFeedSource — the REAL odds source, in two modes:
//
//  (a) PASSIVE: listen for the RequestHelper iframe's relayed `http-api-response`
//      messages (`/Live/GetTopLiveEvents`) and merge them. Works with zero extra
//      requests — just what the widget already fetches (the global top-live feed).
//
//  (b) FULL-FEED (active): the widget sends its requests via
//      `iframe.contentWindow.postMessage`. We shadow that to capture ONE real
//      request as a TEMPLATE (it carries the operator's auth: partnerId, langId,
//      countryCode, message envelope), then replay it — `GetTopLiveSports` to
//      enumerate sports, then `GetTopLiveEvents(sportId, count)` per sport — to pull
//      the ENTIRE live board (all countries/leagues), not just the global top-N.
//      This is what makes real geo-personalization possible (all football, then
//      rank the operator's home market). Degrades to passive if the template can't
//      be captured. (docs/digitain-ws-capture-plan.md — Phase 5.)
// One selectable leg for a booking mint: the Digitain stake + its event.
export interface BookingStake { stakeId: number; eventId: number }

/** Where a stake stands in the freshest copy of the board we hold.
 *  `unknown` = the event carries no readable markets, so we cannot tell — it is
 *  deliberately NOT folded into `gone`. */
export type StakeLiveness = "live" | "moved" | "frozen" | "gone" | "no-event" | "unknown";

export interface StakeState {
  state: StakeLiveness;
  /** Current odds when the stake is still priced; null otherwise. */
  odds: number | null;
  /** Age of the event record the answer came from; null when there is no record. */
  ageMs: number | null;
}

/** A live read of the MATCH, beside `StakeState`'s live read of the PRICE.
 *
 * The 2s reprice ticker could already see a moving price and nothing else, which
 * is how a card ended up showing 1—0 and «ведёт 1:0» under a price that had gone
 * 1.26 → 5.65 on a match that was actually 1:1 (owner screenshot 2026-08-17).
 *
 * ⛔ Every field is nullable and null means "we do not know" — never a zero or an
 * empty string. A caller comparing "" to a real score would see a change on every
 * event we have not captured yet. */
export interface MatchState {
  score: string | null;
  clock: LiveMatch["clock"] | null;
  /** Age of the event record this answer came from; null when there is no record. */
  ageMs: number | null;
}

const MINT_TIMEOUT_MS = 4000;

/** Read the langId a captured http-api request carries (finalParams.data.langId
 * and/or a ["langId", n] pair in finalParams.params). Null when absent. */
function templateLangId(msg: Record<string, unknown>): string | null {
  try {
    const fp = msg.finalParams as Record<string, unknown> | undefined;
    if (!fp) return null;
    const data = fp.data as Record<string, unknown> | undefined;
    if (data && data.langId != null) return String(data.langId);
    if (Array.isArray(fp.params)) {
      for (const p of fp.params as unknown[]) {
        if (Array.isArray(p) && String((p as unknown[])[0]).toLowerCase() === "langid") {
          return String((p as unknown[])[1]);
        }
      }
    }
  } catch {
    /* malformed message */
  }
  return null;
}

/** Read the operator/geo triple + currency a captured http-api request carries,
 *  so a replayed `/Betting/PlaceBet` inherits the session's real answers rather
 *  than inventing them. Reads `finalParams.data` first, then the `params` pairs
 *  (the two template shapes observed on Parikara). Currency defaults to "TRY"
 *  (Digitain's base short name) when the template does not carry one. */
function templateAuth(msg: Record<string, unknown>): PlaceBetAuth | null {
  try {
    const fp = msg.finalParams as Record<string, unknown> | undefined;
    if (!fp) return null;
    const data = (fp.data as Record<string, unknown> | undefined) ?? {};
    const pair = (key: string): unknown => {
      if (!Array.isArray(fp.params)) return undefined;
      for (const p of fp.params as unknown[]) {
        if (Array.isArray(p) && String((p as unknown[])[0]).toLowerCase() === key) {
          return (p as unknown[])[1];
        }
      }
      return undefined;
    };
    const num = (v: unknown): number | null => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const partnerId = num(data.partnerId ?? pair("partnerid"));
    const langId = num(data.langId ?? pair("langid"));
    const countryCode = String(data.countryCode ?? pair("countrycode") ?? "");
    if (partnerId == null || langId == null || !countryCode) return null;
    const currency = String(
      data.currencyShortName ?? data.currency ?? "TRY",
    );
    return { partnerId, langId, countryCode, currencyShortName: currency };
  } catch {
    return null;
  }
}

/** A fresh UUID for the coupon's `RequestId`. `crypto.randomUUID` is present in
 *  every browser this SDK targets; the fallback covers an odd embed. */
function newRequestId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Map the raw `GetBetBuilderStakesByEvent` event object to the PlaceBet coupon's
 *  `PlaceBetEvent` shape. Team ids are NOT in this payload (the bet-builder board
 *  sends no `Teams`) — they stay null and the coupon builder emits 0, exactly as
 *  `HomeTeamId`/`AwayTeamId` do on the operator's own captured coupon. */
export function mapBetBuilderEvent(e: unknown): PlaceBetEvent | null {
  if (e == null || typeof e !== "object") return null;
  const n = e as Record<string, unknown>;
  const id = Number(n.Id);
  if (!Number.isFinite(id)) return null;
  const str = (v: unknown): string => String(v ?? "");
  const num = (v: unknown): number => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };
  return {
    id,
    date: str(n.D),
    sportId: num(n.SId),
    sportName: str(n.SN),
    sportEn: str(n.ESN),
    countryId: num(n.RId),
    countryEn: str(n.ECtN),
    home: str(n.HT),
    away: str(n.AT),
    homeEn: str(n.EHT),
    awayEn: str(n.EAT),
    tournamentId: num(n.CId),
    tournamentName: str(n.CN),
    tournamentEn: str(n.ECN),
    homeTeamId: null,
    awayTeamId: null,
  };
}

/** Parse a `GetBetBuilderStakesByEvent` response into a board. The response is
 *  `{EV:[…], BBF, MBBF, BBSQ, SS}` — read `res.EV`, NOT a bare array.
 *
 *  ⛔ `EV` IS NOT ONE EVENT. Captured authenticated on Parikara 2026-09-12 for
 *  Манчестер Юн–Манчестер Сити (40338357, 539 KB): **ten** entries, all the same
 *  fixture, each a Digitain SUB-EVENT with its own id and its own market groups —
 *  40338357 the main book (Исход, Двойной шанс, Тотал, Фора, team totals, BTTS,
 *  Счет — 84 stakes), 40575117 the player props (637), others the half and corner
 *  books. `product/sdk/src/testdata/betbuilder-40338357.json` is that payload with
 *  the sub-event bodies trimmed.
 *
 *  We take ONE sub-event, and it must be the one that was ASKED for: the coupon
 *  `/Betting/PlaceBet` builds carries a single `PlaceBetEvent`, so a leg drawn from
 *  another sub-event would be placed under the wrong `EventId`.
 *
 *  ⛔ WHEN AN ID IS GIVEN AND NOTHING MATCHES, REFUSE — do not fall back to `EV[0]`.
 *  `buildBetBuilderLegs` is event-BLIND: it matches on stake id or on
 *  `(stakeTypeId, stakeCode, argument)` against whatever board it is handed, and
 *  every sub-event here carries the same outcome triples (nine of the ten quote
 *  Исход 1/X/2). A fallback board therefore matches happily and places the coupon
 *  under `board.event.id` — the wrong sub-event, silently. A null is a `no-board`
 *  verdict the visitor can see; a wrong-event bet is one nobody catches.
 *  `EV[0]` WAS the requested event in both captures; the fallback survives only for
 *  the no-id call, which cannot be wrong about which event it wanted.
 *
 *  ⛔ Do NOT "widen" this to pool every sub-event's stakes until the operator's own
 *  coupon has been seen carrying legs from two of them. That shape is unknown. */
/** The param spec Digitain's OWN bet-builder tab sends, captured off the wire on
 *  Parikara 2026-09-12 — `HashSet<long>` for the selection (not `List<int>`: stake
 *  ids are 10-digit and the app treats the selection as a set), and the nullable
 *  forms for the scalars. Ours differed on all four; it worked, but there is no
 *  reason to send a different contract than the operator's own client. */
export function betBuilderParamSpec(inherited: Record<string, unknown>): unknown[] {
  const spec: unknown[] = [
    ["selectedStakeIds", "HashSet<long>"], ["eventId", "int?"], ["isLive", "bool?"],
  ];
  for (const k of ["langId", "partnerId", "countryCode"]) {
    if (inherited[k] !== undefined) spec.push([k, k === "countryCode" ? "string" : "int?"]);
  }
  return spec;
}

export function parseBetBuilderBoard(res: unknown, eventId?: number): BetBuilderBoard | null {
  const arr = (res as { EV?: unknown[] } | null)?.EV;
  if (!Array.isArray(arr) || arr.length === 0) return null;
  let node: unknown = arr[0];
  if (Number.isFinite(eventId as number)) {
    const wanted = arr.find((e) => Number((e as Record<string, unknown>)?.Id) === eventId);
    if (!wanted) return null;
    node = wanted;
  }
  const event = mapBetBuilderEvent(node);
  if (!event) return null;
  const stakes: BetBuilderLeg[] = [];
  // ⭐⭐ EVERY SUB-EVENT OF THIS FIXTURE, not just the requested one.
  //
  // The coupon really can carry legs from two of them — MEASURED on a live
  // /Betting/PlaceBet (Parikara 2026-09-20, Милан–Лечче): `Исход П1` from the
  // main book and `Угловые: Тотал Больше 9.5` from sub-event 40714488, both
  // under `ParentEventId 40535029`, priced by the operator at 1.95. That is the
  // condition this function's own comment set for widening, and it is now met.
  //
  // ⛔ The node the CALLER asked for still supplies the coupon header (`event`),
  // because `PlaceBet`'s item-level `EventId` is the FIXTURE. The sub-events
  // contribute stakes only, each carrying where it came from.
  //
  // ⛔ A sub-event is admitted ONLY when it hangs off the requested fixture
  // (`EN` === the node's id). Pooling on nothing but "it was in EV" is how a
  // leg from another fixture would reach the coupon.
  const wanted = Number((node as Record<string, unknown>).Id);
  for (const sub of arr as Array<Record<string, unknown>>) {
    const subId = Number(sub?.Id);
    const parent = Number(sub?.EN ?? sub?.Id);
    if (!Number.isFinite(subId)) continue;
    if (subId !== wanted && parent !== wanted) continue;
    const part = {
      eventId: subId,
      // ⛔ EPTId is NOT unique (both halves are 1) and PeriodId is a THIRD
      // field, neither 0 nor the EPTId — see BetBuilderLeg.subEvent.
      eventPartTypeId: Number(sub.EPTId ?? 0) || 0,
      periodId: Number(sub.P ?? 0) || 0,
      periodName: String(sub.PN ?? ""),
    };
    const stakeTypes = sub.StakeTypes;
    if (!Array.isArray(stakeTypes)) continue;
    for (const st of stakeTypes as Array<Record<string, unknown>>) {
      const typeId = Number(st.Id);
      const typeName = String(st.N ?? "");
      if (!Array.isArray(st.Stakes)) continue;
      for (const l of st.Stakes as Array<Record<string, unknown>>) {
        const stakeId = Number(l.Id);
        if (!Number.isFinite(stakeId)) continue;
        stakes.push({
          stakeId,
          stakeTypeId: Number.isFinite(typeId) ? typeId : 0,
          stakeTypeName: typeName,
          stakeName: String(l.N ?? ""),
          argument: l.A == null ? null : Number(l.A),
          stakeCode: Number(l.SC ?? 0),
          ...(Number.isFinite(Number(l.F)) ? { odds: Number(l.F) } : {}),
          // ⛔ The MAIN book carries no `subEvent`, so a main-book coupon
          // serialises byte-identically to every coupon built before this.
          ...(part.eventPartTypeId === 0 && part.periodId === 0
            ? {}
            : { subEvent: part }),
        });
      }
    }
  }
  return { event, stakes };
}

/** `"<EventPartTypeId>:<PeriodId>"` for a board stake — the same string the
 *  backend puts on a leg's `part`. Empty for the main book, so a main-book key
 *  is unchanged. */
export function partOf(l: { subEvent?: { eventPartTypeId: number; periodId: number } }): string {
  return l.subEvent ? `${l.subEvent.eventPartTypeId}:${l.subEvent.periodId}` : "";
}

/** ⭐ THE SUB-EVENT LADDER, FOR FREE.
 *
 *  `Common/GetEvent` — the ladder the poll posts — carries NO corners and no
 *  cards. MEASURED 2026-09-19: the posted ladder named **740 distinct market
 *  ids** off live boards, including every other period sub-book (halves,
 *  quarters, periods, sets, individual tennis games) and not one corner.
 *  They live in `GetBetBuilderStakesByEvent` instead.
 *
 *  ⛔ AND THAT CALL IS AUTHENTICATED (401 anonymous) and sits on the path
 *  `eventMarkets` calls the pilot's capacity constraint — so this makes NO new
 *  request. It harvests the board the SDK already fetched to PRICE a combo. A
 *  fixture warms up in two steps: a goals combo is priced (board fetched), and
 *  the corners lines ride the next poll.
 *
 *  Only the sub-events a scorer can use are kept — corners (4) and cards (5).
 *  The rest (players, shots, fouls, offsides) have no fact behind them, and a
 *  line nothing can score is weight on the poll body for nobody. */
export const SUB_EVENT_PARTS = new Set([4, 5]);

export function subEventLinesFrom(board: BetBuilderBoard | null): MarketLine[] {
  if (!board) return [];
  const out: MarketLine[] = [];
  for (const st of board.stakes) {
    const sub = st.subEvent;
    if (!sub || !SUB_EVENT_PARTS.has(sub.eventPartTypeId)) continue;
    if (!Number.isFinite(st.odds) || Number(st.odds) <= 1) continue;
    out.push({
      market_id: st.stakeTypeId,
      market_name: sub.periodName ? `${sub.periodName}: ${st.stakeTypeName}` : st.stakeTypeName,
      selection: st.stakeName,
      line: st.argument == null ? null : Number(st.argument),
      stake_id: String(st.stakeId),
      odds: Number(st.odds),
      code: st.stakeCode,
      part: `${sub.eventPartTypeId}:${sub.periodId}`,
    } as MarketLine);
  }
  return out;
}

export function subEventIndexFrom(board: BetBuilderBoard | null): {
  eventOfStake: Map<number, number>; partOfEvent: Map<number, string>;
} {
  const eventOfStake = new Map<number, number>();
  const partOfEvent = new Map<number, string>();
  if (!board) return { eventOfStake, partOfEvent };
  const mainStakeIds = new Set(
    board.stakes.filter((st) => !st.subEvent).map((st) => Number(st.stakeId)),
  );
  for (const st of board.stakes) {
    const sub = st.subEvent;
    if (!sub) continue;
    if (!mainStakeIds.has(Number(st.stakeId))) {
      eventOfStake.set(Number(st.stakeId), sub.eventId);
    }
    partOfEvent.set(sub.eventId, `${sub.eventPartTypeId}:${sub.periodId}`);
  }
  return { eventOfStake, partOfEvent };
}
// ⛔ MARKETS_MAX_PER_MIN is a CEILING, not a forecast: if we are ever asked for
// more than this, something upstream is looping and the operator should not pay
// for it. The raw GetEvent response carries ~103 market groups for a live match
// and is large enough that a loop here is expensive for the VISITOR's connection
// as well as for Digitain. ⛔ No byte figure: the "~306 KB" this comment used to
// quote is not reproducible from anything in the repo (both committed fixtures
// are trimmed captures) — see market-source.ts for what IS measured.
const MARKETS_TTL_MS = 30_000;
// ⛔ How old a cached ladder may be and still be POSTED (measured on parikara
// 2026-09-10: polls run 31–44s apart, the fetch TTL is 30s, so a ladder fetched
// right after one poll was already "expired" at the next and NO event ever
// carried lines — the pre-match open match got one card, every poll). Fetching
// still refreshes after MARKETS_TTL_MS; reading tolerates two poll gaps.
const MARKETS_READ_GRACE_MS = 90_000;
const MARKETS_MAX_PER_MIN = 20;
// ⛔ A CEILING ON THE MAP ITSELF. The pool is 50+ events and only ≤3 are primed
// per poll, so a long session accumulates entries the TTL has already made
// useless. 40 is comfortably above anything one board can prime inside a TTL.
const MARKETS_MAX_CACHED = 40;
// ⛔ The corners/cards ladder ages like any other price. 45s is the pre-match
// verdict cadence (`bb-space.PREMATCH_VERDICT_TTL_MS` drains on that beat), so a
// posted sub-event line is never older than the verdict that would price it.
const SUB_LINES_TTL_MS = 45_000;
const SUB_LINES_MAX = 40;

/** The request iframe as our shadow marks it. State lives on the ELEMENT so a
 *  bundle swapped in later on the same page can take the shadow over. */
type ShadowHost = {
  __bcShadowed?: boolean;
  __bcFeedOwner?: DigitainFeedSource;
  __bcFeedState?: { realCW: Window; origin: string;
    template: Record<string, unknown> | null; authTemplate: Record<string, unknown> | null };
};

export class DigitainFeedSource {
  private marketCache = new Map<string, { at: number; lines: MarketLine[] }>();
  /** Corners/cards lines harvested from bet-builder replies — see
   *  `subEventLinesFrom`. Never fetched for its own sake. */
  private subLines = new Map<string, { at: number; lines: MarketLine[] }>();
  private eventOfStake = new Map<number, number>();
  private partOfEvent = new Map<number, string>();
  // ⛔ The OPEN match's live board entry arrives with `markets: []` — measured
  // 2026-08-31 on Lecce v Roma while the operator's own page showed a full
  // book. Without a priced selection the backend cannot build a card, so the
  // fixture the visitor is looking at loses to anything that has prices.
  // Cached under the SAME TTL as the ladder: a price from two hours ago is
  // worse than no price.
  private resultCache = new Map<string, { at: number; markets: MatchResultMarket[] }>();
  private marketCalls: number[] = [];
  /** HTTP status of the last `fetchBetBuilderStakes` reply — null when no reply
   *  arrived at all. 401 means the operator refused the call for want of a session,
   *  which is what a "no-board" actually was on 2026-09-12. */
  betBuilderStatus: number | null = null;
  /** Whether that call carried identity (a shadowed auth request, or the app JWT).
   *  False plus 401 means we called without a credential we could have had — the
   *  app's JWT was unreadable at that moment (a cold load, or mid-hydration). */
  betBuilderAuthed = false;
  /** How many sub-events the last board reply carried (`EV.length`), null when the
   *  reply had no `EV` array. **0 is the operator saying this fixture has no
   *  bet-builder book** — measured 2026-09-12 on four fixtures, all HTTP 200 with
   *  `{"EV":[],"BBF":0}`. It separates "no book" from "we could not parse the book". */
  betBuilderEvents: number | null = null;

  private events = new Map<string, { m: LiveMatch; ts: number }>();
  private started = false;
  private template: Record<string, unknown> | null = null;
  private messageListener: ((e: MessageEvent) => void) | null = null;
  /** An AUTHENTICATED request caught in flight — the only way to reach `/Account/*`,
   * whose JWT is never in a cookie or in storage. See the capture in
   * `shadowContentWindow`. Null until the operator's own client makes one. */
  private authTemplate: Record<string, unknown> | null = null;
  private realCW: Window | null = null;
  private iframeOrigin = "";
  private shadowHost: ShadowHost | null = null;
  private fanTimer: ReturnType<typeof setInterval> | null = null;
  // Language-switch generation. Fan-out messageIds carry it, so responses to
  // OLD-language requests that land after resetTemplate() are dropped instead of
  // repopulating the pool with stale-language names for up to the TTL.
  private gen = 0;
  // Invalidates awaited work across feed stop/start boundaries.
  private lifecycleGen = 0;
  // Belt for the re-capture race: after a language switch, reject candidate
  // templates still carrying the OLD langId (our own hidden widget's next poll is
  // usually the first request across the shadow, and it was booted with the old
  // language until the catalog re-boots it). Time-boxed so a detection miss can
  // never lock us out of full-feed mode forever.
  private rejectLangId: string | null = null;
  private rejectUntil = 0;
  // messageId → resolver, for request/response round-trips (booking mint). The
  // passive odds feed is fire-and-forget; minting needs to await ITS response.
  // ⛔ The resolver receives the WHOLE relay envelope, not `res`. Resolving `res`
  // alone discarded `ok`/`status`, and that is why a 401, a dropped body, a lost
  // correlation and an unparsable payload all reached the strip as one string
  // ("bet-builder:no-board") — measured 2026-09-12, the real answer was HTTP 401.
  private pending = new Map<string, (msg: unknown) => void>();
  private retryTimers = new Set<ReturnType<typeof setTimeout>>();

  start(): void {
    if (this.started) return;
    this.lifecycleGen++;
    this.started = true;
    try {
      this.messageListener = (e: MessageEvent) => this.onMessage(e);
      window.addEventListener("message", this.messageListener, false);
    } catch {
      return; // no window — DigitainCatalog falls back to the React harvest
    }
    this.tryEnableFullFeed(0);
  }

  stop(): void {
    this.lifecycleGen++;
    if (this.messageListener) {
      try { window.removeEventListener("message", this.messageListener); } catch { /* no window */ }
      this.messageListener = null;
    }
    if (this.fanTimer != null) clearInterval(this.fanTimer);
    this.fanTimer = null;
    for (const timer of this.retryTimers) clearTimeout(timer);
    this.retryTimers.clear();
    for (const resolve of this.pending.values()) resolve(null);
    this.pending.clear();
    this.started = false;
  }

  // --- passive receive + merge (covers both the widget's polls and our fan-out) ---
  private onMessage(e: MessageEvent): void {
    if (!this.started) return;
    try {
      const d = e.data as Record<string, unknown> | null;
      if (!d || d.sender !== "http-api-response") return;
      // Round-trip response (booking mint): resolve the awaiting caller by messageId.
      const mid = d.messageId as string | undefined;
      if (mid && this.pending.has(mid)) {
        const resolve = this.pending.get(mid)!;
        this.pending.delete(mid);
        resolve(d);
        return;
      }
      if (typeof d.url !== "string") return;
      // Our fan-out responses echo the messageId we minted, which carries the
      // generation it was sent under. A response from before the last language
      // switch is stale-language data — drop it.
      if (typeof mid === "string" && mid.indexOf("#g") >= 0) {
        const g = Number(mid.slice(mid.indexOf("#g") + 2).split(":")[0]);
        if (Number.isFinite(g) && g !== this.gen) return;
      }
      const url = d.url as string;
      if (url.indexOf("/Live/GetTopLiveSports") >= 0 && Array.isArray(d.res)) {
        this.onSportsList(d.res as unknown[]);
      } else if (url.indexOf("/Live/GetTopLiveEvents") >= 0 && Array.isArray(d.res)) {
        this.mergeEvents(d.res as unknown[]);
      }
    } catch {
      /* ignore malformed relay messages */
    }
  }

  private mergeEvents(res: unknown[]): void {
    const now = Date.now();
    for (const raw of res) {
      const m = mapApiEvent(raw);
      if (!m) continue;
      // Second gate: the fan-out skips excluded SPORTS, but the widget's own passive
      // requests relay through here too and can carry e-sports we never asked for.
      // Tested against the always-English name so it holds in every locale.
      if (EXCLUDE_SPORT_NAME.test(m.sport_en || m.sport || "")) continue;
      this.events.set(m.id, { m, ts: now });
    }
  }

  /**
   * Re-capture the request template after the user switches language.
   *
   * The template is captured ONCE and replayed forever — and it carries the
   * operator's `langId`, frozen at capture time. So after a switch our full-feed
   * replay kept pulling the OLD language: verified live 2026-07-13, the site read
   * EN while our feed was still returning "أيسلندا. الدوري الممتاز". Team and
   * league names on the cards would stay in the language the page booted in.
   *
   * Dropping the template makes the shadow re-capture the widget's next request,
   * which the sportsbook re-issues with the new langId when it re-renders. The
   * cached events are cleared too, so no stale-language names survive the switch.
   */
  resetTemplate(): void {
    // Remember the language we're leaving so the shadow can refuse to re-freeze
    // on a straggler request that still carries it (time-boxed — see rejectLangId).
    this.rejectLangId = this.template ? templateLangId(this.template) : null;
    this.rejectUntil = this.rejectLangId ? Date.now() + 20000 : 0;
    this.gen++;
    this.template = null;
    this.realCW = null;
    if (this.shadowHost?.__bcFeedState) this.shadowHost.__bcFeedState.template = null;
    this.events.clear();
    if (this.fanTimer != null) {
      clearInterval(this.fanTimer);
      this.fanTimer = null;
    }
    // NOTE: the shadow proxy stays installed — it reads `self.template` live, so
    // nulling the template re-arms capture by itself. Re-wrapping here used to
    // stack a proxy over the previous proxy on every language switch.
    if (this.started) this.tryEnableFullFeed(0);
  }

  /** True while a candidate template still carries the langId we just left.
   * Expires after rejectUntil so a langId we failed to read can't strand us in
   * passive mode. Exposed for tests. */
  isStaleLanguage(msg: Record<string, unknown>): boolean {
    if (!this.rejectLangId || Date.now() > this.rejectUntil) return false;
    const lid = templateLangId(msg);
    return lid != null && lid === this.rejectLangId;
  }

  // --- full-feed: capture the request template, then fan out ---
  private tryEnableFullFeed(attempt: number): void {
    if (!this.started || this.template) return;
    const iframe = this.findRequestHelper();
    if (iframe) this.shadowContentWindow(iframe);
    if (!this.template && attempt < 30) {
      const timer = setTimeout(() => {
        this.retryTimers.delete(timer);
        this.tryEnableFullFeed(attempt + 1);
      }, 2000);
      this.retryTimers.add(timer);
    }
  }

  private findRequestHelper(): HTMLIFrameElement | null {
    const frames = document.querySelectorAll("iframe");
    for (let i = 0; i < frames.length; i++) {
      const f = frames[i] as HTMLIFrameElement;
      try {
        if ((f.src || "").indexOf(DIGITAIN_IFRAME_HINT) >= 0) return f;
      } catch {
        /* cross-origin src read can throw */
      }
    }
    return null;
  }

  private shadowContentWindow(iframe: HTMLIFrameElement): void {
    try {
      const marked = iframe as unknown as ShadowHost;
      if (marked.__bcShadowed) {
        // ⛔ Chrome 2026-10-15: a bundle swapped in by self-update found the old
        // bundle's shadow and gave up — no template, pool 0, a dead widget.
        // Take the shadow over and adopt what it already captured. (Shadows from
        // builds before this one route to their own closure; the server sends
        // the swap signal only to builds that send `X-SDK-Caps: handoff`.)
        marked.__bcFeedOwner = this;
        this.shadowHost = marked;
        const st = marked.__bcFeedState;
        if (st) {
          this.realCW = st.realCW;
          this.iframeOrigin = st.origin;
          if (st.authTemplate) this.authTemplate = st.authTemplate;
          if (st.template && !this.template && !this.isStaleLanguage(st.template)) {
            this.template = st.template;
            this.onTemplateReady();
          }
        }
        return;
      }
      const realCW = iframe.contentWindow;
      if (!realCW) return;
      try { this.iframeOrigin = new URL(iframe.src).origin; } catch { this.iframeOrigin = ""; }
      marked.__bcFeedOwner = this;
      marked.__bcFeedState = { realCW, origin: this.iframeOrigin, template: null, authTemplate: null };
      this.shadowHost = marked;
      const state = marked.__bcFeedState;
      const owner = this;
      const proxy = new Proxy(realCW as object, {
        get(t, p) {
          if (p === "postMessage") {
            return function (this: unknown): unknown {
              try {
                // The feed that owns the page NOW — after a self-update swap that
                // is the new bundle's, not the one that installed this shadow.
                const self = marked.__bcFeedOwner ?? owner;
                const msg = arguments[0] as Record<string, unknown> | undefined;
                const fp = msg && (msg.finalParams as Record<string, unknown> | undefined);
                const data = fp && (fp.data as Record<string, unknown> | undefined);
                if (!self.template && msg && msg.sender === "http-api" && data && data.sportId != null
                    && !self.isStaleLanguage(msg)) {
                  self.template = msg;
                  self.realCW = realCW;
                  state.template = msg;
                  self.onTemplateReady();
                }
                // Second, INDEPENDENT capture on the same shadow: an AUTHENTICATED
                // request. The odds template above is anonymous, so replaying it at
                // an `/Account/*` path sends no token and the operator answers for
                // nobody. This one carries `auth:true` and the Authorization JWT,
                // which lives only in the operator's JS memory — no cookie, no
                // storage — so capturing it in flight is the only way to reach the
                // account surface at all.
                //
                // Deliberately NOT a second Proxy on the same property: two shadows
                // on one `contentWindow` fight, and the loser silently stops seeing
                // traffic. One shadow, two templates.
                if (!self.authTemplate && msg && msg.sender === "http-api" && fp && fp.auth === true
                    && fp.headers && (fp.headers as Record<string, unknown>).Authorization) {
                  self.authTemplate = msg;
                  self.realCW = realCW;
                  state.authTemplate = msg;
                }
              } catch {
                /* never break the widget's own request */
              }
              return (realCW as unknown as { postMessage: (...a: unknown[]) => unknown })
                .postMessage.apply(realCW, arguments as unknown as unknown[]);
            };
          }
          try { return Reflect.get(t, p); } catch { return undefined; }
        },
      });
      Object.defineProperty(iframe, "contentWindow", { get: () => proxy, configurable: true });
      marked.__bcShadowed = true;
    } catch {
      /* if shadowing fails we stay in passive mode */
    }
  }

  private onTemplateReady(): void {
    this.fanOut();
    if (this.fanTimer == null) {
      this.fanTimer = setInterval(() => this.fanOut(), FULLFEED_INTERVAL_MS);
    }
  }

  private fanOut(): void {
    if (!this.started) return;
    // Enumerate every live sport; football pulled directly too (primary geo sport,
    // available before the sport list returns).
    this.sendRequest("Live/GetTopLiveSports", { dropSportId: true, count: 50 });
    this.sendRequest("Live/GetTopLiveEvents", { sportId: 1, count: FULLFEED_COUNT });
  }

  private onSportsList(res: unknown[]): void {
    if (!this.template) return; // passive mode — nothing to fan out
    for (const raw of res) {
      const s = raw as Record<string, unknown>;
      const id = Number(s.Id);
      // EGN is the ALWAYS-ENGLISH sport name; N is localized. Testing N first meant
      // that on an Arabic board the regex saw "رياضات إلكترونية" instead of
      // "Esports", matched nothing, and pulled e-sports + Virtual Sport straight
      // into a football user's pool (verified live 2026-07-13: ids 53 Esports,
      // 94 E-Football, 95 E-Basketball, 96 E-IceHockey, 118 Virtual Sport).
      const name = String(s.EGN ?? s.N ?? "");
      if (!Number.isFinite(id) || id === 1) continue; // football already pulled
      if (EXCLUDE_SPORT_NAME.test(name)) continue; // skip virtual/simulated
      this.sendRequest("Live/GetTopLiveEvents", { sportId: id, count: FULLFEED_COUNT });
    }
  }

  private sendRequest(
    path: string,
    opts: { sportId?: number; count: number; dropSportId?: boolean },
  ): void {
    if (!this.template || !this.realCW) return;
    let clone: Record<string, unknown>;
    try {
      clone = structuredClone(this.template);
    } catch {
      try { clone = JSON.parse(JSON.stringify(this.template)); } catch { return; }
    }
    clone.url = "/" + path;
    const fp = clone.finalParams as Record<string, unknown>;
    fp.path = path;
    const data = (fp.data as Record<string, unknown>) || (fp.data = {});
    data.count = opts.count;
    delete data.stakeTypes; // no market filter → full markets (incl. 2-way id 702)
    if (opts.dropSportId) delete data.sportId;
    else data.sportId = opts.sportId;
    if (Array.isArray(fp.params)) {
      fp.params = (fp.params as unknown[]).filter((p) => {
        const k = Array.isArray(p) ? String((p as unknown[])[0]).toLowerCase() : "";
        if (k === "staketypes") return false;
        if (k === "sportid" && opts.dropSportId) return false;
        return true;
      });
      for (const p of fp.params as unknown[]) {
        if (!Array.isArray(p)) continue;
        const k = String((p as unknown[])[0]).toLowerCase();
        if (k === "count") (p as unknown[])[1] = opts.count;
        if (k === "sportid" && !opts.dropSportId) (p as unknown[])[1] = opts.sportId;
      }
      if (!opts.dropSportId && !(fp.params as unknown[]).some(
        (p) => Array.isArray(p) && String((p as unknown[])[0]).toLowerCase() === "sportid",
      )) {
        (fp.params as unknown[]).push(["sportId", opts.sportId]);
      }
    }
    // The "#g<gen>:" tag lets onMessage drop responses from before a language
    // switch (they'd carry the old langId's names).
    clone.messageId = "/" + path + "#g" + this.gen + ":" + Math.random();
    try {
      this.realCW.postMessage(clone, this.iframeOrigin || "*");
    } catch {
      /* best-effort */
    }
  }

  /**
   * The operator's own JWT, read out of the sport app's settings.
   *
   * Parikara renders Digitain in OUR document, so the app object is right there on
   * `window.sportdigi.mobile` and its `globalSettings.jwt.access_token` is the same
   * 664-char token the `/Account/*` calls send. Verified live 2026-08-13 with a
   * negative control: with the token those calls return the user's favourites; with
   * `auth:false` and no header they never answer at all.
   *
   * This is what makes the ★ usable. Waiting to SHADOW an authenticated request
   * meant the control stayed hidden until the user happened to touch their betslip
   * or open Фавориты — a cold load produces zero authenticated calls, so on the
   * page they actually land on, the feature did not exist.
   *
   * Re-read on every call, never cached: the app refreshes this token, and a stale
   * copy would fail exactly when the session is longest.
   */
  private accountToken(): string | null {
    try {
      const app = (window as unknown as {
        sportdigi?: { mobile?: { config?: { globalSettings?: { jwt?: { access_token?: unknown } } } } };
      }).sportdigi;
      const t = app?.mobile?.config?.globalSettings?.jwt?.access_token;
      return typeof t === "string" && t.length > 40 ? t : null;
    } catch {
      return null;
    }
  }

  /**
   * Is the account surface reachable? Needs a replay channel and SOME proof of
   * identity: either a token we can read, or an authenticated request we shadowed.
   * Logged out, both are absent and the caller hides the control.
   */
  canCallAccount(): boolean {
    if (!this.realCW) return false;
    if (this.authTemplate) return true;
    return !!(this.template && this.accountToken());
  }

  /**
   * The operator's OWN bet-builder gate, read off `globalSettings`.
   *
   * The vendor's own tab handler switches to Bet Builder only when
   * `loggedIn || IsBetBuilderEnabledForLoggedOutUser || tab !== betBuilder`
   * (read out of the live bundle, partner 722, 2026-09-12).
   *
   * ⛔ MEASURED FOR A HYDRATED, LOGGED-IN USER on Parikara 2026-09-12:
   * `user.Id 33140913`, `IsBetBuilderEnabled true`, `IsLiveBetBuilderEnabled true`,
   * `partner.IsBetBuilderEnabledForLoggedOutUser false`. **The feature is enabled
   * for this operator's users.** The board's 401 was our own credential-less call,
   * not a missing entitlement.
   *
   * ⚠️ READ IT LATE, NOT AT LOAD. The same page a few seconds earlier answers
   * `user.Id 0`, `jwt null`, `IsBetBuilderEnabled false` — the app is still
   * hydrating, and a whole wrong diagnosis was written on that snapshot
   * (post-mortem §10, withdrawn by §11).
   *
   * ⚠️ Reported, NOT gated on: `false` is indistinguishable from "asked too early",
   * so gating here would switch the feature off for anyone whose first tap beats
   * hydration. The reported values are what a gate would eventually be built on.
   */
  betBuilderGate(): {
    userId: number | null; enabled: boolean | null; liveEnabled: boolean | null;
    loggedOutAllowed: boolean | null;
  } {
    const bool = (v: unknown) => (typeof v === "boolean" ? v : null);
    try {
      const gs = (window as unknown as {
        sportdigi?: { mobile?: { config?: { globalSettings?: Record<string, unknown> } } };
      }).sportdigi?.mobile?.config?.globalSettings;
      const user = gs?.user as Record<string, unknown> | undefined;
      const partner = gs?.partner as Record<string, unknown> | undefined;
      return {
        // A numeric STRING id counts too, or a build that sends "33140913" reads unreadable.
        userId: user?.Id === undefined || user?.Id === null || user?.Id === ""
          ? null : (Number.isFinite(Number(user.Id)) ? Number(user.Id) : null),
        enabled: bool(user?.IsBetBuilderEnabled),
        liveEnabled: bool(user?.IsLiveBetBuilderEnabled),
        loggedOutAllowed: bool(partner?.IsBetBuilderEnabledForLoggedOutUser),
      };
    } catch {
      return { userId: null, enabled: null, liveEnabled: null, loggedOutAllowed: null };
    }
  }

  /**
   * The four inputs behind `canCallAccount()`, plus the verdict it returned.
   *
   * Reported so the ★'s absence is answerable. Two people reported it missing on
   * 2026-09-01 and every candidate cause — logged out, nothing shadowed yet, an
   * anonymous template with no readable token — produces the same silence:
   * `star_match` simply never fires. See star-gate.ts for how the pair of readings
   * separates "never" from "not yet".
   *
   * ⛔ `can` is `canCallAccount()`, CALLED — not the same expression written out a
   * second time. A probe that recomputes its subject stops measuring it the first
   * time the subject changes.
   */
  starGate(): StarGateRead {
    return {
      cw: !!this.realCW,
      tpl: !!this.template,
      auth: !!this.authTemplate,
      jwt: !!this.accountToken(),
      can: this.canCallAccount(),
    };
  }

  /**
   * Replay the captured AUTHENTICATED template at another `/Account/*` path.
   *
   * Resolves with the operator's parsed response, or null on timeout / no template.
   *
   * ⚠️ A resolved value is NOT proof the call did anything. `/Account/
   * AddLiveEventFavorite` answers `ok:true, status:200, res:""` for an event id it
   * then ignores (measured live 2026-08-13 with a sub-event id). Callers that need
   * to know whether the write LANDED must read the list back — see
   * `DigitainFavourites.setEventStarred`. This is the same lesson as the coupon's
   * `Deleted` flag: the operator says yes and does nothing.
   */
  callAccount(path: string, data: Record<string, unknown>, timeoutMs = 6000): Promise<unknown> {
    // ⚠️ NOT wrapped in a `new Promise` around `awaitReply`. That adds one extra
    // microtask hop between the reply and the caller's `await`, which is enough
    // to change how many budgeted round-trips a caller completes inside one
    // awaited pass — `bet-history.test.ts` counts exactly that and went 15 → 14.
    if (!this.realCW) return Promise.resolve(null);
    // Prefer a shadowed authenticated request (it already carries a header the
    // operator built). Otherwise upgrade the anonymous odds template with the
    // token read from the app — the path that makes this work on a cold load.
    const token = this.accountToken();
    const source = this.authTemplate ?? (token ? this.template : null);
    if (!source) return Promise.resolve(null);
    let clone: Record<string, unknown>;
    try {
      clone = structuredClone(source);
    } catch {
      try { clone = JSON.parse(JSON.stringify(source)); } catch { return Promise.resolve(null); }
    }
    clone.url = "/" + path;
    const fp = clone.finalParams as Record<string, unknown>;
    fp.path = path;
    fp.method = "post";
    if (!this.authTemplate && token) {
      fp.auth = true;
      fp.headers = { ...(fp.headers as Record<string, unknown> | undefined), Authorization: "Bearer " + token };
    }
    // Keep the template's own langId/partnerId/countryCode — they are the
    // operator's, not ours to invent — and layer the call's fields on top.
    const base = (fp.data as Record<string, unknown>) || {};
    fp.data = { ...base, ...data };
    // The param spec describes the OLD call's arguments; a stale one makes the
    // helper drop the fields we just set.
    fp.params = Object.keys(data).map((k) => [k, "int?"]);
    return this.awaitReply(clone, "bcacct:" + path + ":" + Math.random(), timeoutMs);
  }

  /** Post one prepared request through the shadowed iframe and resolve with its
   *  relayed response, or null on timeout / postMessage failure.
   *
   *  The plumbing only — every caller decides its own template, method and body.
   *  Shared so the account replay and the pre-match replay cannot drift apart on
   *  the part that is pure mechanics (correlate by messageId, clear the timer if
   *  the reply beats it, never leave an entry in `pending`). */
  private awaitReply(
    clone: Record<string, unknown>, id: string, timeoutMs: number, envelope = false,
  ): Promise<unknown> {
    const lifecycleGen = this.lifecycleGen;
    return new Promise((resolve) => {
      if (!this.realCW) return resolve(null);
      clone.messageId = id;
      const done = setTimeout(() => {
        if (this.pending.has(id)) { this.pending.delete(id); resolve(null); }
      }, timeoutMs);
      this.pending.set(id, (msg) => {
        clearTimeout(done);
        if (!this.started || lifecycleGen !== this.lifecycleGen) {
          resolve(null);
          return;
        }
        resolve(envelope ? msg : ((msg as { res?: unknown } | null)?.res ?? null));
      });
      try {
        this.realCW.postMessage(clone, this.iframeOrigin || "*");
      } catch {
        clearTimeout(done);
        this.pending.delete(id);
        resolve(null);
      }
    });
  }

  /**
   * Replay the ANONYMOUS odds template at another public path, and await the reply.
   *
   * ⛔ Not `callAccount`: that one forces `method: "post"` and attaches the
   * account JWT. These are anonymous GETs against the pre-match board, and
   * sending a bearer token with them would be gratuitous.
   *
   * ⚠️ Only `langId` / `partnerId` / `countryCode` are inherited from the
   * template — never the whole body. The captured template is a LIVE odds
   * request, so its `sportId` / `count` / `checkIsActiveAndBetStatus` mean
   * nothing here, and those three are the operator's own answers (the language
   * the board is rendered in, who the partner is, and what geo the operator
   * decided this session is in) which we must not invent. Verified live
   * 2026-08-28: a pre-match request rebuilt this way from a live template
   * returned 200 and a full board.
   */
  /**
   * One event's totals ladder. ⛔ Called ONLY for events we are about to show:
   * this is a real request against the operator's API and /suggestions is the
   * pilot's capacity constraint.
   *
   * ⛔ Returns the NORMALISED lines, never the raw payload. Measured 2026-08-31
   * over the committed 20-line fixture: 2,496 B serialised (the 8-line one is
   * 1,000 B). The raw response is far larger — ~103 market groups on a live
   * match — but the "~306 KB" / "~900 KB" figures quoted here previously were
   * never measured against anything in this repo. Do not rely on them.
   */
  async eventMarkets(eventId: string, maxAgeMs: number = MARKETS_TTL_MS): Promise<MarketLine[]> {
    if (!this.started) return [];
    const lifecycleGen = this.lifecycleGen;
    const now = Date.now();
    const hit = this.marketCache.get(eventId);
    if (hit && now - hit.at < Math.min(maxAgeMs, MARKETS_TTL_MS)) return hit.lines;
    this.marketCalls = this.marketCalls.filter((t) => now - t < 60_000);
    // A hard ceiling. Serve the stale entry rather than making the call.
    if (this.marketCalls.length >= MARKETS_MAX_PER_MIN) return hit ? hit.lines : [];
    // A hidden tab parses nothing for nobody (probe 2026-09-10: a ~2 MB
    // GetEvent every poll in a background tab). The read grace keeps what
    // is cached; the next visible poll refreshes.
    if (typeof document !== "undefined" && document.hidden === true) return hit ? hit.lines : [];
    this.marketCalls.push(now);
    let res: unknown = null;
    try {
      // ⛔ 20s, not the 8s default: a pre-match GetEvent is ~2 MB (1,076 stakes
      // on Fenerbahçe–Roma) and the probe saw Como's ladder never posted while
      // the page was busy — a timed-out reply is dropped and the cache stays
      // empty until the next poll's fetch, which times out the same way.
      res = await this.callPublic(
        "Common/GetEvent",
        { eventId: Number(eventId) },
        [["eventId", "int"]],
        20_000,
      );
    } catch {
      // A market fetch that fails must never break the board. An empty ladder
      // means "no opinion", which is the same as no coverage.
      return hit ? hit.lines : [];
    }
    if (!this.started || lifecycleGen !== this.lifecycleGen) return hit ? hit.lines : [];
    const lines = normaliseEvent(res);
    this.resultCache.set(eventId, { at: now, markets: normaliseMatchResult(res) });
    this.marketCache.set(eventId, { at: now, lines });
    // ⛔ BOUND IT. The pool is 50+ events and a full ladder serialises to
    // 2,496 B (measured over the committed 20-line fixture, not estimated), so
    // an unbounded map re-attaches everything ever primed to every 5s poll —
    // against the endpoint that is the pilot's capacity constraint.
    // Oldest-first eviction: the TTL makes old entries useless anyway.
    if (this.marketCache.size > MARKETS_MAX_CACHED) {
      const oldest = [...this.marketCache.entries()]
        .sort((a, b) => a[1].at - b[1].at)[0];
      if (oldest) this.marketCache.delete(oldest[0]);
    }
    return lines;
  }

  /** The ladder we already have for this event, WITHOUT fetching.
   *
   * ⛔ Cache-only by design. The board is assembled on the hot path and must
   * never wait on the operator's API — a market we have not primed yet simply
   * means "no opinion", which is the same as no coverage. */
  /** The match-result market for this event, fetched with the ladder.
   *  Same TTL: never serve a price old enough to have moved. */
  cachedMatchResultFor(eventId: string): MatchResultMarket[] {
    const hit = this.resultCache.get(eventId);
    if (!hit || Date.now() - hit.at >= MARKETS_TTL_MS) return [];
    return hit.markets;
  }

  cachedMarketsFor(eventId: string, maxAgeMs: number = MARKETS_READ_GRACE_MS): MarketLine[] {
    const hit = this.marketCache.get(eventId);
    // ⛔ THE SAME TTL AS THE FETCH PATH. eventMarkets checks the age before
    // refetching; this read path, used on EVERY 5s poll, checked nothing — so
    // once an event was primed its ladder was posted for the life of the page,
    // long after priming stopped (it drops out of the top-3, the per-minute
    // ceiling is hit, or the GetEvent call throws). The backend selects line,
    // odds and STAKE_ID from these lines, and a stake id names a PRICE: a
    // re-opened market mints a new one, Digitain ACCEPTS the old one and flags
    // Deleted:true. An absent ladder is already a safe, well-handled state.
    if (!hit || Date.now() - hit.at >= maxAgeMs) return [];
    return hit.lines;
  }

  private callPublic(
    path: string,
    data: Record<string, unknown>,
    paramSpec: Array<[string, string]>,
    timeoutMs = 8000,
  ): Promise<unknown> {
    if (!this.template || !this.realCW) return Promise.resolve(null);
    let clone: Record<string, unknown>;
    try {
      clone = structuredClone(this.template);
    } catch {
      try { clone = JSON.parse(JSON.stringify(this.template)); } catch { return Promise.resolve(null); }
    }
    clone.url = "/" + path;
    const fp = clone.finalParams as Record<string, unknown>;
    fp.path = path;
    fp.method = "get";
    const base = (fp.data as Record<string, unknown>) || {};
    // The operator/geo triple travels in `data` on the templates captured from
    // Parikara, and in `params` on the ones `mintBooking` was built against.
    // Carry BOTH rather than picking one: an operator whose template puts them
    // in the other place would otherwise get a board in the wrong language, or
    // no board at all, with nothing in the console to say why.
    const auth = (p: unknown) =>
      Array.isArray(p) && AUTH_PARAM_KEYS.has(String((p as unknown[])[0]).toLowerCase());
    const inherited: Record<string, unknown> = {};
    for (const k of ["langId", "partnerId", "countryCode"]) {
      if (base[k] !== undefined) inherited[k] = base[k];
    }
    fp.data = { ...data, ...inherited };
    // The param spec describes the CALL's arguments; the live one left in place
    // would describe sportId/count and nothing we are actually sending.
    const kept = Array.isArray(fp.params) ? (fp.params as unknown[]).filter(auth) : [];
    fp.params = (paramSpec as unknown[]).concat(kept);
    return this.awaitReply(clone, "bcpre:" + path + ":" + Math.random(), timeoutMs);
  }

  /**
   * Pull the operator's PRE-MATCH board — the fixtures that have not started.
   *
   * Three calls, in order, all captured live on 2026-08-28 and recorded with real
   * payloads in `product/docs/digitain/prematch-board-capture.md`:
   *
   *   1. `Prematch/GetTournamentGroups` — the operator's own competition groups.
   *      Parikara returns exactly one, «Популярные соревнования» / "Popular
   *      competitions". ⛔ Matched on `EnglishName`, never on the localized `Name`
   *      and never on a hard-coded id: the id is per-operator, and keying on the
   *      localized name is the mistake that silently killed prestige, geo and
   *      flags elsewhere in this file.
   *   2. `Prematch/GetTournamentsByGroupId` — the tournaments in that group.
   *   3. `Common/GetMixedSportAndEventsListWithOutright` PER TOURNAMENT.
   *      ⛔ `tournamentId` is REQUIRED. Asking for "everything in the next 24
   *      hours" with no tournament returns `[]`, not a board — which is why this
   *      is a fan-out and not one call.
   *
   * This is the operator's OWN top-tier list, which is a stronger claim than our
   * hand-written `pool.core.league_slugs` guess: they authored it.
   *
   * Resolves with [] on any failure — no template yet, a group that does not
   * answer, a malformed relay. The widget then behaves exactly as it does today.
   */
  async fetchPrematchBoard(
    horizonMinutes = 1440,
    // ⭐ `all` pulls EVERY group the operator publishes, not just their
    // «Popular competitions». Measured 2026-08-28: the fixtures taking the most
    // money sat in Argentina Primera Division and Uruguay Primera Division —
    // neither is a popular-competitions tournament, so neither was ever pulled,
    // so neither could ever carry a fixture name.
    //
    // ⛔ Every extra tournament is one more request against the OPERATOR'S
    // production API. Widening the scope is safe only alongside
    // `prematch_pull_share`, which decides how many sessions run the fan-out at
    // all — see index.ts.
    scope: "popular" | "all" = "popular",
    maxTournaments = PREMATCH_MAX_TOURNAMENTS,
  ): Promise<LiveMatch[]> {
    if (!this.template || !this.realCW) return [];
    const groups = await this.callPublic(
      "Prematch/GetTournamentGroups",
      { applicationType: PREMATCH_APPLICATION_TYPE },
      [["applicationType", "int?"]],
    );
    if (!Array.isArray(groups) || groups.length === 0) return [];
    const popular = groups.find((g) => {
      const n = g as Record<string, unknown>;
      return PREMATCH_POPULAR_GROUP.test(String(n.EnglishName ?? ""));
    }) as Record<string, unknown> | undefined;
    // No group NAMED popular → take them all rather than nothing: another
    // operator may label its group differently, and an empty pool is the exact
    // failure this whole feature exists to fix.
    const chosen = (scope === "all" || !popular)
      ? (groups as Record<string, unknown>[])
      : [popular];

    const now = Date.now();
    const startIso = new Date(now).toISOString();
    const endIso = new Date(now + horizonMinutes * 60000).toISOString();

    const tournaments: Array<Record<string, unknown>> = [];
    for (const g of chosen) {
      const res = await this.callPublic(
        "Prematch/GetTournamentsByGroupId",
        {
          groupId: String(g.Id ?? ""),
          tournamentStart: startIso,
          tournamentEnd: endIso,
          // ⚠️ INERT here, and measured to be so (2026-08-28): over the SAME
          // explicit window, `timeFilter` 0 / 4 / 8 returned the identical 8
          // tournaments with identical event counts. The operator's UI sends
          // the value matching its own filter chip, which makes it look like
          // the selector; the DATES are. Sent as 0 to say "no extra filter".
          timeFilter: 0,
          applicationType: PREMATCH_APPLICATION_TYPE,
        },
        [["groupId", "int?"], ["tournamentStart", "DateTime?"], ["tournamentEnd", "DateTime?"],
         ["timeFilter", "int?"], ["applicationType", "int?"]],
      );
      if (Array.isArray(res)) tournaments.push(...(res as Record<string, unknown>[]));
      if (tournaments.length >= maxTournaments) break;
    }

    const out: LiveMatch[] = [];
    const seen = new Set<string>();
    const eventsOf = async (tournamentId: string, fromIso: string, toIso: string): Promise<void> => {
      const res = await this.callPublic(
        "Common/GetMixedSportAndEventsListWithOutright",
        {
          startDate: fromIso,
          endDate: toIso,
          period: 0,
          tournamentId,
          // Match-winner markets only, exactly as the live fan-out trims: the
          // full book is ~84 markets per event and the card needs one.
          stakeTypes: [1, 702],
          isTournament: false,
          eventFilterType: false,
          includeLiveEvents: false,
        },
        [["startDate", "DateTime?"], ["endDate", "DateTime?"], ["period", "int?"],
         ["tournamentId", "int?"], ["stakeTypes", "List<int>"], ["isTournament", "bool?"],
         ["eventFilterType", "EventFilterType?"], ["includeLiveEvents", "bool?"]],
      );
      // ⚠️ TWO SHAPES from one endpoint: a board comes back as an OBJECT whose
      // `events` is the list; an empty result comes back as a bare `[]`.
      const events = (res && !Array.isArray(res) && typeof res === "object")
        ? (res as Record<string, unknown>).events
        : null;
      if (!Array.isArray(events)) return;
      for (const raw of events) {
        const m = mapPrematchEvent(raw as Record<string, unknown>);
        if (!m || seen.has(m.id)) continue;
        seen.add(m.id);
        out.push(m);
      }
    };

    // ⭐ THE LAST HOUR, BY PERIOD (2026-09-25). The group route above can only
    // ever see the operator's «Popular competitions» — ONE group, 12 leagues on
    // capture day. Measured over 7 days: 5,674 bets on 2,158 fixtures were placed
    // in the last hour before kickoff, and 6.4% of those fixtures were ever on our
    // board, while that hour converts best (M3: 271 bets/1k views at 10–30 min).
    // This is the operator's own «1 час» filter, replayed as captured
    // (docs/digitain/prematch-board-capture.md, "By-period chain"): sport →
    // regions → tournaments → events, `period` 5 over a 60-minute window. Own cap,
    // so it never competes with the week for the group route's slots.
    const soonEndIso = new Date(now + Math.min(horizonMinutes, PREMATCH_SOON_MINUTES) * 60000).toISOString();
    const soonT: string[] = [];
    const byPeriod = {
      startDate: startIso, endDate: soonEndIso, period: PREMATCH_SOON_PERIOD,
      isTournament: false,
    };
    for (const sportId of PREMATCH_SOON_SPORT_IDS) {
      if (soonT.length >= PREMATCH_SOON_MAX_TOURNAMENTS) break;
      const regions = await this.callPublic(
        "Common/GetMixedChampionshipListByPeriod",
        { ...byPeriod, sportId: String(sportId), eventFilterType: 0, includeLiveSports: false },
        [["startDate", "DateTime?"], ["endDate", "DateTime?"], ["period", "int?"], ["sportId", "int?"],
         ["isTournament", "bool?"], ["eventFilterType", "EventFilterType?"], ["includeLiveSports", "bool?"]],
      );
      if (!Array.isArray(regions)) continue;
      for (const r of regions as Record<string, unknown>[]) {
        if (soonT.length >= PREMATCH_SOON_MAX_TOURNAMENTS) break;
        if (r == null || r.Id == null) continue;
        const res = await this.callPublic(
          "Common/GetMixedTournamentsByPeriod",
          { ...byPeriod, championshipId: Number(r.Id), includeLiveTournaments: false },
          [["startDate", "DateTime?"], ["endDate", "DateTime?"], ["period", "int?"],
           ["championshipId", "int?"], ["isTournament", "bool?"], ["includeLiveTournaments", "bool?"]],
        );
        const tl = res && !Array.isArray(res) && typeof res === "object"
          ? (res as Record<string, unknown>).TL : null;
        if (!Array.isArray(tl)) continue;
        for (const t of tl as Record<string, unknown>[]) {
          const id = String(t?.Id ?? "");
          if (!id || soonT.includes(id)) continue;
          soonT.push(id);
          if (soonT.length >= PREMATCH_SOON_MAX_TOURNAMENTS) break;
        }
      }
    }
    for (const id of soonT) await eventsOf(id, startIso, soonEndIso);

    for (const t of tournaments.slice(0, maxTournaments)) {
      // Skip the sports we would drop anyway — one fewer request against the
      // OPERATOR'S production API, which is the cost this fan-out actually has.
      if (EXCLUDE_SPORT_NAME.test(String(t.EnglishSportName ?? t.SportName ?? ""))) continue;
      await eventsOf(String(t.TournamentId ?? ""), startIso, endIso);
    }
    return out;
  }

  /**
   * Mint a Digitain booking number for the given stakes, then it can be injected
   * into the betslip via SportFrame.setCouponFromBookingNumber. Reuses the captured
   * RequestHelper template (operator auth) and replays a POST to `Betting/BookAbet`
   * through the proxy — the ONLY context Akamai trusts (a server-side POST is 403'd).
   * Verified live: anonymous, no user session needed. Returns the BookNumber, or null
   * on timeout / non-zero StatusCode / missing template (caller falls back to navigate).
   */
  mintBooking(stakes: BookingStake[], isCombo: boolean): Promise<number | null> {
    if (!this.template || !this.realCW || stakes.length === 0) return Promise.resolve(null);
    let clone: Record<string, unknown>;
    try {
      clone = structuredClone(this.template);
    } catch {
      try { clone = JSON.parse(JSON.stringify(this.template)); } catch { return Promise.resolve(null); }
    }
    clone.url = "/Betting/BookAbet";
    const fp = clone.finalParams as Record<string, unknown>;
    fp.path = "Betting/BookAbet";
    fp.method = "post";
    fp.data = {
      SystemType: 0,
      BetType: isCombo ? 3 : 1, // 3 = combo/multi, 1 = single (verified live for combo)
      IsContainLive: true,      // our Digitain recommendations are live-only
      IsContainLine: false,
      StakesItem: stakes.map((s) => ({
        StakeId: s.stakeId, EventId: s.eventId, PromoId: 0, BetBuilderId: 0,
      })),
    };
    // Keep only the operator/geo auth pairs; drop odds-list query params.
    if (Array.isArray(fp.params)) {
      fp.params = (fp.params as unknown[]).filter((p) => {
        const k = Array.isArray(p) ? String((p as unknown[])[0]).toLowerCase() : "";
        return k === "langid" || k === "partnerid" || k === "countrycode";
      });
    }
    const mid = "/Betting/BookAbet#" + Math.random();
    clone.messageId = mid;

    return new Promise<number | null>((resolve) => {
      const timer = setTimeout(() => {
        this.pending.delete(mid);
        resolve(null);
      }, MINT_TIMEOUT_MS);
      this.pending.set(mid, (msg: unknown) => {
        clearTimeout(timer);
        const r = ((msg as { res?: unknown } | null)?.res ?? null) as
          { BookNumber?: number; StatusCode?: number } | null;
        const bn = r && typeof r.BookNumber === "number" ? r.BookNumber : null;
        resolve(bn && r?.StatusCode === 0 ? bn : null);
      });
      try {
        (this.realCW as Window).postMessage(clone, this.iframeOrigin || "*");
      } catch {
        clearTimeout(timer);
        this.pending.delete(mid);
        resolve(null);
      }
    });
  }

  /**
   * Place a same-match bet-builder combo (bet builder / same-game acca) on the
   * operator's slip.
   *
   * POSTs `/Betting/PlaceBet` — the endpoint the NATIVE UI uses (captured live
   * 2026-09-10 on a real "Bet Builder (2)" bet), NOT `BookAbet`. `BookAbet` only
   * mints a booking number for `setCouponFromBookingNumber`, a bridge with 0
   * occurrences in the iframe bundles — it can never complete a bet. `PlaceBet`
   * is the endpoint that actually places it; this is the request side the SDK
   * was missing.
   *
   * Resolves with the OrderNumber, or null on a missing template, a non-placed
   * response, or a timeout.
   *
   * ⛔ THIS PLACES A REAL BET. Gate it behind an explicit user confirm — never
   * fire it from a card render or a poll.
   */
  placeBetBuilder(
    ev: PlaceBetEvent,
    legs: BetBuilderLeg[],
    amount: number,
    factor: number,
  ): Promise<PlaceBetResult | null> {
    if (!this.template || !this.realCW || legs.length === 0) return Promise.resolve(null);
    const auth = templateAuth(this.template);
    if (!auth) return Promise.resolve(null);
    let clone: Record<string, unknown>;
    try {
      clone = structuredClone(this.template);
    } catch {
      try { clone = JSON.parse(JSON.stringify(this.template)); } catch { return Promise.resolve(null); }
    }
    const fp = clone.finalParams as Record<string, unknown>;
    clone.url = "/Betting/PlaceBet";
    fp.path = "Betting/PlaceBet";
    fp.method = "post";
    fp.data = buildPlaceBetCoupon(ev, legs, amount, factor, auth, newRequestId());
    // Keep only the operator/geo auth pairs; drop any odds-list query params.
    if (Array.isArray(fp.params)) {
      fp.params = (fp.params as unknown[]).filter((p) => {
        const k = Array.isArray(p) ? String((p as unknown[])[0]).toLowerCase() : "";
        return k === "langid" || k === "partnerid" || k === "countrycode";
      });
    }
    const mid = "/Betting/PlaceBet#" + Math.random();
    return this.awaitReply(clone, mid, MINT_TIMEOUT_MS).then((res) => {
      const r = res as {
        OrderNumber?: number;
        IsBetPlaced?: boolean;
        StatusException?: string | null;
        StatusMessage?: { Status?: string; Code?: number; Params?: unknown } | null;
      } | null;
      if (!r) return null;
      const order = r.IsBetPlaced === true && typeof r.OrderNumber === "number" ? r.OrderNumber : null;
      if (order != null) return { order, status: null };
      // The operator refused — surface WHY so the caller can name the reason.
      let status: string | null = null;
      if (r.StatusException) status = String(r.StatusException);
      else if (r.StatusMessage && (r.StatusMessage.Status || r.StatusMessage.Params)) {
        status = r.StatusMessage.Status ?? JSON.stringify(r.StatusMessage.Params);
      }
      return { order: null, status };
    });
  }

  /**
   * Fetch the bet-builder stakes for one event — the same `GetBetBuilderStakesByEvent`
   * the native "Bet Builder" tab loads — mapped to the PlaceBet coupon shape.
   *
   * This is the enrichment step for a bet-builder combo: the suggestion carries only
   * stake ids + odds; this returns the full `PlaceBetEvent` (sport/country/tournament
   * ids, names) and every stake's market metadata (stake code, type, argument) that
   * `placeBetBuilder` needs. Resolves null on a missing template or a malformed relay.
   */
  async fetchBetBuilderStakes(
    eventId: number, isLive = false, selected: number[] = [],
  ): Promise<BetBuilderBoard | null> {
    const reply = await this.betBuilderReply(eventId, isLive, selected);
    const board = parseBetBuilderBoard(reply, eventId);
    const index = subEventIndexFrom(board);
    for (const [stakeId, subEventId] of index.eventOfStake) {
      if (this.eventOfStake.size >= 5000) this.eventOfStake.clear();
      this.eventOfStake.set(stakeId, subEventId);
    }
    for (const [subEventId, part] of index.partOfEvent) {
      if (this.partOfEvent.size >= 5000) this.partOfEvent.clear();
      this.partOfEvent.set(subEventId, part);
    }
    // ⭐ Keep the corners/cards ladder this reply happened to carry. Costs
    // nothing — the request was made to price a combo either way.
    const sub = subEventLinesFrom(board);
    if (sub.length) {
      if (this.subLines.size > SUB_LINES_MAX) this.subLines.clear();
      this.subLines.set(String(eventId), { at: Date.now(), lines: sub });
    }
    return board;
  }

  /** The sub-event lines last harvested for this fixture, or `[]`.
   *  ⛔ Cache-only and TTL'd like every other ladder: a stale corners price is
   *  worse than none, because the chooser would propose a line the book has
   *  moved. */
  subEventLinesFor(eventId: string, maxAgeMs: number = SUB_LINES_TTL_MS): MarketLine[] {
    const hit = this.subLines.get(String(eventId));
    if (!hit || Date.now() - hit.at > maxAgeMs) return [];
    return hit.lines;
  }

  subEventIdForStake(stakeId: number): number | null {
    return this.eventOfStake.get(stakeId) ?? null;
  }

  partOfSubEvent(eventId: number): string | null {
    return this.partOfEvent.get(eventId) ?? null;
  }

  /** The raw `GetBetBuilderStakesByEvent` envelope — `{EV, BBF, MBBF, BBSQ, SS}` —
   *  shared by the board parse and by `betBuilderFactor`, which needs `BBF` and not
   *  the board at all. Records the call's own verdict (status / authed / EV count).
   *  Resolves null on a missing template, a failed clone, or no reply. */
  private async betBuilderReply(
    eventId: number, isLive: boolean, selected: number[],
  ): Promise<unknown> {
    // ⛔ Clear the verdict FIRST. These are instance fields, so an early return below
    // would leave the PREVIOUS call's reading standing and the fork would report
    // `unauthed http=401` for a tap that never sent anything.
    this.betBuilderStatus = null;
    this.betBuilderAuthed = false;
    this.betBuilderEvents = null;
    if (!this.template || !this.realCW) return null;
    let clone: Record<string, unknown>;
    try {
      clone = structuredClone(this.template);
    } catch {
      try { clone = JSON.parse(JSON.stringify(this.template)); } catch { return null; }
    }
    // ⛔ This endpoint is a POST, not a GET (captured live 2026-09-11): body is
    // {selectedStakeIds:[], eventId, isLive, langId, partnerId, countryCode}. A GET
    // (the callPublic path) returned nothing — that was the "no-board" failure.
    clone.url = "/Common/GetBetBuilderStakesByEvent";
    const fp = clone.finalParams as Record<string, unknown>;
    fp.path = "Common/GetBetBuilderStakesByEvent";
    fp.method = "post";
    const base = (fp.data as Record<string, unknown>) || {};
    const inherited: Record<string, unknown> = {};
    for (const k of ["langId", "partnerId", "countryCode"]) {
      if (base[k] !== undefined) inherited[k] = base[k];
    }
    fp.data = { selectedStakeIds: selected, eventId, isLive, ...inherited };
    // The param spec describes the CALL's arguments; a stale one would describe
    // sportId/count. ⛔ Types are the OPERATOR'S OWN — captured off its bet-builder
    // tab 2026-09-12, see `betBuilderParamSpec`. (The body went out complete under
    // every spec tried, so the spec was never the failure — but do not send a lie.)
    fp.params = betBuilderParamSpec(inherited);
    // ⛔⛔ THIS ENDPOINT IS AUTHENTICATED. Measured on the wire 2026-09-12 (Parikara,
    // event 40338354): the POST leaves the page with a complete body under every
    // param spec and answers **HTTP 401**, while the same replay at a GET-only path
    // answers 404 — so the route exists, the body is fine, and the call is simply
    // refused for want of a session. The anonymous odds template can never satisfy
    // it. Attach identity the way `callAccount` does: a shadowed authenticated
    // request if we caught one, otherwise the app's own JWT.
    const token = this.accountToken();
    if (this.authTemplate) {
      // ⚠️ REPLACES the odds template's headers rather than merging, unlike the token
      // branch below — deliberate. The result is a hybrid (odds template's
      // finalParams, auth template's headers), which no other caller builds, but the
      // captured auth headers are a complete authenticated set by construction (see
      // shadowContentWindow: an authTemplate is only kept when it already carries
      // `auth:true` + Authorization) and merging the anonymous template's headers
      // under them would add nothing and could contradict them.
      const auth = (this.authTemplate.finalParams as Record<string, unknown>) || {};
      fp.auth = true;
      if (auth.headers) fp.headers = { ...(auth.headers as Record<string, unknown>) };
    } else if (token) {
      fp.auth = true;
      fp.headers = { ...(fp.headers as Record<string, unknown> | undefined), Authorization: "Bearer " + token };
    }
    const reply = await this.awaitReply(
      clone, "bcpre:Common/GetBetBuilderStakesByEvent:" + Math.random(), 8000, true,
    ) as { res?: unknown; status?: unknown; ok?: unknown } | null;
    // Keep WHY, so the fork can say "unauthed" / "http 500" / "no reply" instead of
    // collapsing four different causes into one "no-board".
    this.betBuilderAuthed = fp.auth === true;
    if (reply) {
      // ⛔ A reply with no numeric status is still a REPLY. Reporting it as null
      // would read as "no reply at all", which is the opposite diagnosis; -1 says
      // "answered, status unreadable" and `ok:false` is the only thing we know.
      this.betBuilderStatus = typeof reply.status === "number"
        ? reply.status
        : (reply.ok === false ? -1 : null);
      const ev = (reply.res as { EV?: unknown } | null)?.EV;
      this.betBuilderEvents = Array.isArray(ev) ? ev.length : null;
    }
    return reply?.res ?? null;
  }

  /**
   * ⭐ THE OPERATOR'S OWN PRICE FOR A SELECTION — and its verdict on whether the legs
   * combine at all. Resolves the combined factor, 0 when the operator refuses the
   * combination, or null when we could not tell (no reply, no board).
   *
   * ⛔ `BBF` on the envelope IS that factor. MEASURED on the live book 2026-09-12,
   * Наполи–Болонья 40343484, one POST per row:
   *
   *   | selectedStakeIds            | BBF  |
   *   | []                          | 0    |
   *   | [П1]                        | 1.85 |
   *   | [П1, Тотал Больше 2.5]      | 3.05 |  ← the operator's own UI showed 3.05
   *   | [П1, X]  (mutually exclusive) | 0  |
   *
   * ⛔ Do NOT infer combinability from which stakes come back: with a selection the
   * operator returns the SAME 77 stakes every time — X and П2 included. Presence proves
   * nothing; a first attempt at this oracle read presence and would have called every
   * pair combinable.
   *
   * ⭐ The PRICE is the answer, not the presence. MEASURED 2026-09-14 in the app's own Bet
   * Builder tab (Брентфорд–Челси 40502522, testdata/betbuilder-selected-40502522.json):
   * after selecting П2 every joinable outcome is re-rendered with its COMBO price
   * (Больше 2.5 1.52 → 2.91) and every refused one with none — П1, X, all double chance,
   * the handicaps П2 implies, all full-time scores. The "zeroes every F" reading above was
   * one fixture on the day the wrong `EV` sub-event was still being parsed; it does not
   * hold. ⚠️ The app's call does not pass through the top document's fetch/XHR.
   *
   * ⛔ And do NOT price a same-match acca by multiplying its legs. These outcomes are
   * correlated and the book prices them accordingly: 1.85 × 2.14 = 3.96, the operator
   * says 3.05. Multiplying overstates the return by 30% on this pair alone.
   */
  async betBuilderFactor(
    eventId: number, isLive: boolean, stakeIds: number[],
  ): Promise<number | null> {
    const ids = stakeIds.filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length < 2) return null;
    const reply = await this.betBuilderReply(eventId, isLive, ids);
    const bbf = (reply as { BBF?: unknown } | null)?.BBF;
    return typeof bbf === "number" ? bbf : null;
  }

  /** `betBuilderFactor` as a yes/no. True = the operator prices this combination,
   *  false = it refuses it, **null = we could not tell** — and null is NOT a refusal;
   *  a caller that blocks on null blocks every visitor whose reply was slow. */
  async betBuilderCombinable(
    eventId: number, isLive: boolean, stakeIds: number[],
  ): Promise<boolean | null> {
    const f = await this.betBuilderFactor(eventId, isLive, stakeIds);
    return f == null ? null : f > 0;
  }

  getEvents(): LiveMatch[] {
    const now = Date.now();
    const out: LiveMatch[] = [];
    for (const [id, rec] of this.events) {
      if (now - rec.ts > FULLFEED_EVENT_TTL_MS) {
        this.events.delete(id);
        continue;
      }
      out.push(rec.m);
    }
    return out;
  }

  /**
   * What the FRESHEST copy of the board says about one stake, right now.
   *
   * The pool a suggestion was ranked from is re-pulled every FULLFEED_INTERVAL_MS
   * (45s) and an event survives FULLFEED_EVENT_TTL_MS (100s) without being re-seen.
   * A live line can be suspended inside that window — so a card can look freshly
   * validated while the stake under it is already dead. This re-reads the stake at
   * the moment of the add, which is the only reading that can tell a stale pick
   * apart from a rejected post.
   *
   * `ageMs` is the age of the EVENT record, i.e. how stale this answer itself is.
   */
  stakeState(eventId: number, stakeId: number, expectedOdds: number | null): StakeState {
    const rec = this.events.get(String(eventId));
    if (!rec) return { state: "no-event", odds: null, ageMs: null };
    const ageMs = Date.now() - rec.ts;
    const markets = rec.m.markets;
    // No markets is NOT evidence of removal — React-harvested events legitimately
    // carry none (see hasBettableWinnerPair). Saying "unknown" keeps this instrument
    // from manufacturing the very confirmation it exists to test for.
    if (!markets || markets.length === 0) return { state: "unknown", odds: null, ageMs };
    const want = String(stakeId);
    // A priced read anywhere beats a suspension anywhere: the same stake id appears
    // in both the 3-way and the 2-way book, so a per-market answer would report a
    // re-opened line as locked purely because the other market was scanned first.
    for (const mk of markets) {
      for (const sel of mk.selections) {
        if (sel.stake_id !== want) continue;
        const moved = expectedOdds != null && Math.abs(sel.odds - expectedOdds) > 0.001;
        return { state: moved ? "moved" : "live", odds: sel.odds, ageMs };
      }
    }
    // Still on the board, just unpriced — the book has taken the line down for now.
    for (const mk of markets) {
      if (mk.suspended && mk.suspended.indexOf(want) >= 0) {
        return { state: "frozen", odds: null, ageMs };
      }
    }
    return { state: "gone", odds: null, ageMs };
  }

  /** The live score/clock for an event, or nulls when we have not captured it. */
  matchState(eventId: number): MatchState {
    const rec = this.events.get(String(eventId));
    if (!rec) return { score: null, clock: null, ageMs: null };
    const score = String((rec.m as LiveMatch).score || "").trim();
    return {
      score: score || null,
      clock: (rec.m as LiveMatch).clock ?? null,
      ageMs: Date.now() - rec.ts,
    };
  }

  /** The stake id currently carrying this OUTCOME, or null if the board isn't
   * offering it right now. See `resolveOutcome` on DigitainCatalog for why. */
  resolveOutcome(o: OutcomeRef): { stakeId: string; odds: number } | null {
    const rec = this.events.get(String(o.eventId));
    if (!rec) return null;
    return matchOutcome(rec.m.markets, o);
  }

  ready(): boolean {
    return this.getEvents().length > 0;
  }
}

/** An outcome named the way it survives a stake-id rotation. */
export interface OutcomeRef {
  eventId: number;
  /** Digitain StakeType id — 1 = Исход (1X2), 702 = 2-way, etc. */
  marketId: number;
  /** Digitain `SC` / the coupon's `StakeCode`. */
  code: number;
  /** Digitain `A` / the coupon's `Argument`; null for markets with no line. */
  argument: number | null;
}

/** Find the live selection for an outcome inside an already-parsed market list.
 *
 * Only `selections` is searched — never `suspended`. A suspended stake is exactly
 * what we must NOT hand back: re-posting one is how the dead leg got into the
 * coupon in the first place. No match means "not on offer right now", and the
 * caller's job is then to say so, not to substitute something else. */
function matchOutcome(markets: Market[] | undefined, o: OutcomeRef): { stakeId: string; odds: number } | null {
  if (!markets) return null;
  const wantArg = o.argument == null ? null : Number(o.argument);
  for (const mk of markets) {
    if (String(mk.id) !== String(o.marketId)) continue;
    for (const sel of mk.selections) {
      if (sel.code == null || Number(sel.code) !== Number(o.code)) continue;
      const selArg = sel.argument == null ? null : Number(sel.argument);
      if (selArg !== wantArg) continue;
      return { stakeId: sel.stake_id, odds: sel.odds };
    }
  }
  return null;
}

export class DigitainCatalog {
  private events: LiveMatch[] = [];
  private feed = new DigitainFeedSource();
  private booted = false;
  private harvestTimer: ReturnType<typeof setInterval> | null = null;
  private retryTimers = new Set<ReturnType<typeof setTimeout>>();
  private stopped = false;
  // Language the hidden widget should boot with after a switch. Wins over
  // setts.language, which some operators leave stale (or empty — Parikara).
  private langOverride: string | null = null;

  // Bootstrapper + SportFrame.setts may not be ready when the SDK first inits
  // (the sportsbook is still booting). Retry until they are, then boot once.
  start(attempt = 0): void {
    if (this.stopped || this.booted) return;
    // Listen for the iframe's relayed API responses BEFORE booting the widget, so
    // the /Live/GetTopLiveEvents payloads it triggers are captured from the first poll.
    this.feed.start();
    const w = window as unknown as {
      Bootstrapper?: { bootLandingWidgets?: (cfg: unknown) => unknown };
      SportFrame?: { setts?: Record<string, string> };
    };
    const B = w.Bootstrapper;
    const setts = w.SportFrame?.setts;
    if (!B || typeof B.bootLandingWidgets !== "function" || !setts?.server || !setts?.sportPartner) {
      if (attempt < 20) {
        const timer = setTimeout(() => {
          this.retryTimers.delete(timer);
          this.start(attempt + 1);
        }, 1000);
        this.retryTimers.add(timer);
      }
      return; // not ready yet — retry, else degrade to current-match only
    }

    let c = document.getElementById(CATALOG_CONTAINER_ID);
    if (!c) {
      c = document.createElement("div");
      c.id = CATALOG_CONTAINER_ID;
      // rendered but off-screen and inert
      c.style.cssText = "position:fixed;left:-99999px;top:0;width:400px;height:1200px;opacity:0;pointer-events:none;";
      document.body.appendChild(c);
    }

    try {
      B.bootLandingWidgets({
        server: setts.server,
        // After a language switch the override is the freshest truth — the widget
        // must poll with the NEW langId or the shadow re-captures a stale-language
        // template from our own poll (the locale-switch bug eating its own fix).
        defaultLanguage: this.langOverride || setts.language || "en",
        sportPartner: setts.sportPartner,
        apps: [{ name: "LiveEventWidget", containerId: CATALOG_CONTAINER_ID }],
      });
      this.booted = true;
    } catch {
      return;
    }

    // Harvest after the widget renders, then refresh periodically. Single timer —
    // a locale-switch re-boot must not stack a second interval.
    const delayedHarvest = setTimeout(() => {
      this.retryTimers.delete(delayedHarvest);
      this.harvest();
    }, 4000);
    this.retryTimers.add(delayedHarvest);
    if (this.harvestTimer == null) {
      this.harvestTimer = setInterval(() => this.harvest(), 30000);
    }
  }

  stop(): void {
    this.events = [];
    this.stopped = true;
    for (const timer of this.retryTimers) clearTimeout(timer);
    this.retryTimers.clear();
    if (this.harvestTimer != null) clearInterval(this.harvestTimer);
    this.harvestTimer = null;
    this.feed.stop();
    this.booted = false;
    document.getElementById(CATALOG_CONTAINER_ID)?.remove();
  }

  /** Fetch the totals ladder for the events we just SHOWED, so the next poll
   * can use it.
   *
   * ⛔ WARM CACHE, one cycle behind, and that is deliberate. Ranking happens on
   * the server, so the SDK cannot know which events will be shown until after
   * it has posted. Fetching for the whole pool is impossible: getCandidates()
   * returns 50+ events, so priming the pool would mean 50+ real requests against
   * the operator's API every refresh. Priming the ≤3 events that were actually
   * shown costs at most 3 calls per poll. (An earlier comment put a "~15 MB per
   * refresh" figure on it, derived from an unmeasured ~306 KB per response —
   * the call COUNT is the argument here; the byte figure was never measured.)
   *
   * ⚠️ The consequence: the FIRST time an event is shown it has no ladder, so a
   * fact-selected market can only appear from the second cycle onward. */
  /** ⭐ RESOLVES WHEN THE LADDERS ARE IN HAND. It used to return void, and the
   *  only thing that read the result was whatever poll happened next — measured
   *  on parikara 2026-09-18, a match page polled at +2.1s, +11.1s, +42.1s and
   *  +127.2s, so a ladder that landed just after a poll waited up to 85 seconds
   *  to be posted, and the open fixture's same-match accas with it. A caller can
   *  now poll on arrival instead of on the next tick. Never rejects: one
   *  fixture's failed fetch must not drop the others. */
  primeMarkets(eventIds: string[], maxAgeMs?: number): Promise<void> {
    const jobs: Promise<unknown>[] = [];
    for (const id of eventIds.slice(0, 3)) {
      if (!id) continue;
      jobs.push(this.feed.eventMarkets(String(id), maxAgeMs).catch(() => null));
    }
    return Promise.all(jobs).then(() => undefined);
  }

  /** The ladder already cached for this event, or []. Never fetches. */
  marketsFor(eventId: string, maxAgeMs?: number): MarketLine[] {
    return this.feed.cachedMarketsFor(String(eventId), maxAgeMs);
  }

  /** The match-result market already cached for this event, or []. Never
   *  fetches. Used to give the OPEN match a price when its board entry has
   *  none — see the call site in index.ts. */
  cachedMatchResultFor(eventId: string): MatchResultMarket[] {
    return this.feed.cachedMatchResultFor(String(eventId));
  }

  getCandidates(): LiveMatch[] {
    // Prefer the real feed (full odds book at the source); fall back to the React
    // harvest only if the relay hasn't delivered yet.
    const feedEvents = this.feed.getEvents();
    const pool = feedEvents.length > 0 ? feedEvents : this.events;
    // Frozen lines last: a match whose winner market lost a side becomes an
    // un-bettable "watch" pick — the backend caps those to one slot, but sorting
    // them to the back here keeps the ranked pool full of real picks first.
    // Stable partition (not sort) — relative order within each group is preserved.
    const bettable = pool.filter((m) => hasBettableWinnerPair(m));
    const frozen = pool.filter((m) => !hasBettableWinnerPair(m));
    return bettable.concat(frozen);
  }

  /** The user switched language: re-capture the feed template so events come back
   * in the NEW language (its langId is frozen at capture — see resetTemplate) AND
   * re-boot the hidden widget — it was booted with the old language, and without
   * the re-boot its next 30s poll is usually the first request across the shadow,
   * re-freezing the template on the OLD langId.
   *
   * `next` is our locale code; Digitain has no Turkmen and serves Turkish, so tk
   * boots the widget as "tr". */
  onLocaleChange(next?: string): void {
    this.events = [];
    this.feed.resetTemplate();
    if (next) this.langOverride = next === "tk" ? "tr" : next;
    if (this.booted) {
      const c = document.getElementById(CATALOG_CONTAINER_ID);
      if (c) c.innerHTML = ""; // drop the old-language widget instance
      this.booted = false;
      this.start();
    }
  }

  mintBooking(stakes: BookingStake[], isCombo: boolean): Promise<number | null> {
    return this.feed.mintBooking(stakes, isCombo);
  }

  /** Place a same-match bet-builder combo — see DigitainFeedSource.placeBetBuilder. */
  placeBetBuilder(ev: PlaceBetEvent, legs: BetBuilderLeg[], amount: number, factor: number): Promise<PlaceBetResult | null> {
    return this.feed.placeBetBuilder(ev, legs, amount, factor);
  }

  /** Fetch the bet-builder stakes for one event — see DigitainFeedSource.fetchBetBuilderStakes. */
  fetchBetBuilderStakes(
    eventId: number, isLive = false, selected: number[] = [],
  ): Promise<BetBuilderBoard | null> {
    return this.feed.fetchBetBuilderStakes(eventId, isLive, selected);
  }

  /** Corners/cards lines harvested from a bet-builder reply — see
   *  `DigitainFeedSource.subEventLinesFor`. Cache-only, never fetches. */
  subEventLinesFor(eventId: string, maxAgeMs?: number): MarketLine[] {
    return this.feed.subEventLinesFor(eventId, maxAgeMs);
  }

  subEventIdForStake(stakeId: number): number | null {
    return this.feed.subEventIdForStake(stakeId);
  }

  partOfSubEvent(eventId: number): string | null {
    return this.feed.partOfSubEvent(eventId);
  }

  /** Does the operator say these legs combine? — see
   *  DigitainFeedSource.betBuilderCombinable. null means "could not tell". */
  betBuilderCombinable(
    eventId: number, isLive: boolean, stakeIds: number[],
  ): Promise<boolean | null> {
    return this.feed.betBuilderCombinable(eventId, isLive, stakeIds);
  }

  /** The operator's own combined price for a selection (0 = it refuses the
   *  combination, null = could not tell) — see DigitainFeedSource.betBuilderFactor. */
  betBuilderFactor(
    eventId: number, isLive: boolean, stakeIds: number[],
  ): Promise<number | null> {
    return this.feed.betBuilderFactor(eventId, isLive, stakeIds);
  }

  /** Why the last bet-builder board failed: the reply's HTTP status (null = no
   *  reply) and whether the call carried identity. See DigitainFeedSource. */
  betBuilderFailure(): { status: number | null; authed: boolean; events: number | null } {
    return {
      status: this.feed.betBuilderStatus,
      authed: this.feed.betBuilderAuthed,
      events: this.feed.betBuilderEvents,
    };
  }

  /** The operator's own bet-builder gate — see DigitainFeedSource.betBuilderGate. */
  betBuilderGate(): ReturnType<DigitainFeedSource["betBuilderGate"]> {
    return this.feed.betBuilderGate();
  }

  /** Is the sport app logged in? `user.Id > 0` yes, `0` no, unreadable null.
   *  ⚠️ Reads `false` while the app hydrates — see betBuilderGate. Only the
   *  boolean leaves the page, never the id. */
  loggedIn(): boolean | null {
    const id = this.feed.betBuilderGate().userId;
    return id === null || !Number.isFinite(id) ? null : id > 0;
  }

  /**
   * The stake id the board is CURRENTLY using for an outcome we hold a dead id for.
   *
   * A stake id identifies a price, not an outcome. When Digitain pulls a market and
   * re-opens it, the same outcome returns under a new id — and a coupon still
   * holding the old one is not merely stale, it is unplaceable: the row greys under
   * "Доступность событий изменилась" and the Принять button cannot revive it
   * (reproduced live on Parikara 2026-08-13). `(marketId, code, argument)` is the
   * part of the identity that survives, so a repair re-resolves through it.
   *
   * Returns null when the outcome is not on offer right now — which is a real and
   * common answer, not a failure: markets like "Исход с 1-50 мин." expire outright
   * and never come back (10 of 10 vanished stakes in one live sample had no
   * successor). The caller must handle null by reporting the add failed, NEVER by
   * substituting a different outcome than the user was shown.
   */
  resolveOutcome(o: OutcomeRef): { stakeId: string; odds: number } | null {
    const fromFeed = this.feed.resolveOutcome(o);
    if (fromFeed) return fromFeed;
    const m = this.events.find((e) => e.id === String(o.eventId));
    return m ? matchOutcome(m.markets, o) : null;
  }

  /** The account surface, forwarded from the feed source that owns the shadowed
   * iframe and therefore the captured authenticated template. */
  canCallAccount(): boolean {
    return this.feed.canCallAccount();
  }

  /** The ★ gate's own inputs, forwarded from the feed source that owns them. */
  starGate(): StarGateRead {
    return this.feed.starGate();
  }

  callAccount(path: string, data: Record<string, unknown>): Promise<unknown> {
    return this.feed.callAccount(path, data);
  }

  /** The operator's PRE-MATCH board, forwarded from the feed source that owns the
   *  shadowed iframe and therefore the captured template. Resolves [] whenever the
   *  replay is not available — the widget then behaves exactly as it does today. */
  fetchPrematchBoard(horizonMinutes?: number): Promise<LiveMatch[]> {
    return this.feed.fetchPrematchBoard(horizonMinutes);
  }

  /** Re-read one stake at add time — see DigitainFeedSource.stakeState. Falls back
   *  to the React harvest, which has no timestamps (ageMs stays null there). */
  stakeState(eventId: number, stakeId: number, expectedOdds: number | null): StakeState {
    const fromFeed = this.feed.stakeState(eventId, stakeId, expectedOdds);
    if (fromFeed.state !== "no-event") return fromFeed;
    const m = this.events.find((e) => e.id === String(eventId));
    if (!m) return fromFeed;
    const markets = m.markets;
    if (!markets || markets.length === 0) return { state: "unknown", odds: null, ageMs: null };
    const want = String(stakeId);
    for (const mk of markets) {
      for (const sel of mk.selections) {
        if (sel.stake_id !== want) continue;
        const moved = expectedOdds != null && Math.abs(sel.odds - expectedOdds) > 0.001;
        return { state: moved ? "moved" : "live", odds: sel.odds, ageMs: null };
      }
    }
    for (const mk of markets) {
      if (mk.suspended && mk.suspended.indexOf(want) >= 0) {
        return { state: "frozen", odds: null, ageMs: null };
      }
    }
    return { state: "gone", odds: null, ageMs: null };
  }

  /** Feed first, React harvest second — same precedence as `stakeState`. The
   *  harvest carries no timestamps, so `ageMs` stays null on that path. */
  matchState(eventId: number): MatchState {
    const fromFeed = this.feed.matchState(eventId);
    if (fromFeed.score != null || fromFeed.clock != null) return fromFeed;
    const m = this.events.find((e) => e.id === String(eventId));
    if (!m) return fromFeed;
    const score = String(m.score || "").trim();
    return { score: score || null, clock: m.clock ?? null, ageMs: null };
  }

  /**
   * What the board we already hold knows about a stake id ALONE — no event id,
   * because that is all `BettingBusWatcher` has for a leg the user picked
   * themselves (the receipt names it only by stake id and accepted price).
   *
   * Scans the current candidate pool for a market selection with a matching
   * stake id. `is_live` is always true here: `getCandidates()` only ever holds
   * events harvested from the LiveEventWidget — there is no pre-match pool.
   */
  /** What either board knows about an EVENT id — the live catalogue first, then
   * the pre-match board handed in by the integration.
   *
   * ⛔ `is_live` is a MEASURED answer here, not an assumption: a hit on the live
   * catalogue is live, a hit on the pre-match board is not, and a miss returns
   * null so the leg records its event id and claims nothing. That last case is
   * what keeps "100% of bets are live" from being an artefact of the only board
   * we could ever resolve against. */
  eventFacts(eventId: number): EventFacts | null {
    const want = String(eventId);
    for (const m of this.getCandidates()) {
      if (m.id === want) {
        return {
          sport_en: m.sport_en, league_en: m.league_en,
          country_en: m.country_en, is_live: true,
          ...(m.home_en ? { home_en: m.home_en } : {}),
          ...(m.away_en ? { away_en: m.away_en } : {}),
        };
      }
    }
    for (const m of this.prematchBoard()) {
      if (m.id === want) {
        return {
          sport_en: m.sport_en, league_en: m.league_en,
          country_en: m.country_en, is_live: false,
          ...(m.starts_at ? { starts_at: m.starts_at } : {}),
          ...(m.home_en ? { home_en: m.home_en } : {}),
          ...(m.away_en ? { away_en: m.away_en } : {}),
        };
      }
    }
    return null;
  }

  /** The pre-match board, supplied by the integration (it owns the puller's
   * timer). Defaults to empty, so a non-Digitain operator and any session with
   * the feature off behave exactly as before. */
  prematchBoard: () => LiveMatch[] = () => [];

  stakeFacts(stakeId: number): StakeFacts | null {
    const want = String(stakeId);
    for (const m of this.getCandidates()) {
      const markets = m.markets;
      if (!markets) continue;
      for (const mk of markets) {
        for (const sel of mk.selections) {
          if (sel.stake_id !== want) continue;
          return {
            sport_en: m.sport_en,
            league_en: m.league_en,
            country_en: m.country_en,
            is_live: true,
            selection: sel.name,
          };
        }
      }
    }
    return null;
  }

  ready(): boolean {
    return this.feed.ready() || this.events.length > 0;
  }

  private harvest(): void {
    if (this.stopped) return;
    const c = document.getElementById(CATALOG_CONTAINER_ID);
    if (!c) return;
    const comp = c.querySelector("sport-live") ?? c.querySelector("*");
    const root = (comp && (comp as { shadowRoot?: ShadowRoot }).shadowRoot) || comp;
    if (!root) return;
    try {
      const harvested = harvestReactEvents(root as unknown as { querySelectorAll: (s: string) => ArrayLike<unknown> });
      // Same virtual/e-sport gate as the feed paths. This fallback pool serves the
      // first seconds after boot AND every language switch (feed cleared) — without
      // the filter those are exactly the windows e-sports slipped into a football
      // user's board.
      const filtered = harvested.filter((m) => !EXCLUDE_SPORT_NAME.test(m.sport_en || m.sport || ""));
      if (filtered.length > 0) this.events = filtered;
    } catch {
      // leave last-good events in place
    }
  }
}
